// ══════════════════════════════════════════════════════
// Strategy Lab — Main Orchestrator (Redesigned v2 FULL)
// ══════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from "react";
import { useApi } from "../../hooks/useApi";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Pill, Button, Metric, SectionTitle, LoadingState, formatPnl } from "../../components/ui";
import { toast, showModal, SkeletonCards, EmptyIcon, SortTh, sortRows } from "../../components/shared";
import { API_BASE } from "../../config";
import {
  ALL_INSTRUMENTS, TFS, INST_META, PIPELINE_STATUS_ORDER,
  OPTIMIZER_PHASES, ok, fDate, fDateShort, postApi, getApi, deleteApi,
  bestParamsToApplyData, buildConsensusItems, inp, sel, lbl, grpH, grpDesc,
} from "./constants";
import PipelineStepper from "./PipelineStepper";
import BuilderTab from "./BuilderTab";
import AnalyzeTab from "./AnalyzeTab";
import DeployTab from "./DeployTab";
import { TrialChart } from "./Charts";
import { WFBars } from "./Charts";
import DiffPreview, { buildDiffFromParams, buildDiffFromConsensus } from "./DiffPreview";
import { RunCard, BtDetail, BtCompare, OptDetail, WFDetail, ScanDetail } from "./DetailViews";
import { TuningSummary } from "./ParameterTuning";
import VerificationPanel from "./VerificationPanel";

/* ── Elapsed Timer ───────────────────────────── */
function ElapsedTimer({ running }) {
  const [seconds, setSeconds] = useState(0);
  const startRef = useRef(Date.now());
  useEffect(() => {
    if (!running) return;
    startRef.current = Date.now();
    setSeconds(0);
    const id = setInterval(() => setSeconds(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [running]);
  if (!running || seconds < 1) return null;
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  return <span style={{ fontSize:12, fontFamily:fonts.mono, color:theme.text3, fontWeight:600 }}>
    {h > 0 ? `${h}h ` : ""}{m > 0 ? `${m}m ` : ""}{s}s
  </span>;
}

/* ── Shared Primitives ───────────────────────── */
function F({label,children,span}){ return <div style={{gridColumn:span?`span ${span}`:undefined}}><label style={{...lbl,fontSize:10}}>{label}</label>{children}</div>; }
function Tog({checked,onChange,label}){
  return <div style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}} onClick={()=>onChange(!checked)}>
    <div style={{width:36,height:20,borderRadius:10,background:checked?theme.green:theme.border,transition:"background 0.2s",position:"relative",flexShrink:0}}>
      <div style={{width:16,height:16,borderRadius:8,background:"#fff",position:"absolute",top:2,left:checked?18:2,transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.15)"}}/>
    </div>
    {label && <span style={{fontSize:12,fontWeight:600,color:theme.text2}}>{label}</span>}
  </div>;
}
function RR({label,min,max,step,vMin,vMax,setMin,setMax}){
  return <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
    <F label={`${label} Min`}><input type="number" value={vMin} onChange={e=>setMin(Number(e.target.value))} step={step} min={min} max={max} style={inp} className="sl-input"/></F>
    <F label={`${label} Max`}><input type="number" value={vMax} onChange={e=>setMax(Number(e.target.value))} step={step} min={min} max={max} style={inp} className="sl-input"/></F>
  </div>;
}
function Empty({icon,title,sub,btn,onClick}) {
  return <Card style={{padding:44,textAlign:"center"}} className="sl-fadein">
    <div style={{display:"flex",justifyContent:"center",marginBottom:16}}><EmptyIcon type={icon}/></div>
    <div style={{fontSize:16,fontWeight:700,marginBottom:5}}>{title}</div>
    <div style={{fontSize:13,color:theme.text3,maxWidth:420,margin:"0 auto 18px"}}>{sub}</div>
    {btn && <Button primary className="sl-btn" onClick={onClick}>{btn}</Button>}
  </Card>;
}

/* ═══ OPT RANGES PANEL ═══════════════════════════ */

/* ═══ BT PARAM PANEL ═════════════════════════════ */
function BtParamPanel({params,setParams,show,onToggle}){
  const s=(k,v)=>setParams(p=>({...p,[k]:v}));
  if(!show)return<button onClick={onToggle} style={{fontSize:12,fontWeight:600,color:theme.blueText,background:"none",border:"none",cursor:"pointer",padding:"4px 0",marginBottom:12,textDecoration:"underline"}}>⚙ Parameter-Overrides</button>;
  return<Card style={{marginBottom:18}} className="sl-fadein">
    <CardHeader title="Parameter-Overrides" sub="Leere Felder = YAML-Default" right={<Button onClick={onToggle}>Einklappen</Button>}/>
    <div style={{padding:"12px 18px"}}>
      <div style={grpH}>Risk & Exit</div><div style={grpDesc}>SL, TP und Risiko überschreiben.</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:14}}>
        <F label="Risk %"><input type="number" step="0.1" value={params.risk_pct??""} onChange={e=>s("risk_pct",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="SL Mult"><input type="number" step="0.1" value={params.sl_multiplier??""} onChange={e=>s("sl_multiplier",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="TP1 RR"><input type="number" step="0.5" value={params.tp1_rr??""} onChange={e=>s("tp1_rr",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="TP2 RR"><input type="number" step="0.5" value={params.tp2_rr??""} onChange={e=>s("tp2_rr",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="TP3 RR"><input type="number" step="0.5" value={params.tp3_rr??""} onChange={e=>s("tp3_rr",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="TP4 RR"><input type="number" step="0.5" value={params.tp4_rr??""} onChange={e=>s("tp4_rr",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
      </div>
      <div style={grpH}>Structure & Filters</div><div style={grpDesc}>SMC, Regime, EMA, Trailing.</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:10}}>
        <F label="ADX"><input type="number" step="5" value={params.adx_threshold??""} onChange={e=>s("adx_threshold",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="EMA Len"><input type="number" value={params.ema_length??""} onChange={e=>s("ema_length",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="BE RR"><input type="number" step="0.25" value={params.be_trigger_rr??""} onChange={e=>s("be_trigger_rr",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="Trail ATR"><input type="number" step="0.5" value={params.trailing_atr_mult??""} onChange={e=>s("trailing_atr_mult",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="Ctx Swing"><input type="number" value={params.context_swing_length??""} onChange={e=>s("context_swing_length",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
        <F label="Entry Swing"><input type="number" value={params.entry_swing_length??""} onChange={e=>s("entry_swing_length",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8}}>
        <F label="Break"><select value={params.break_confirm??""} onChange={e=>s("break_confirm",e.target.value||undefined)} style={sel}><option value="">YAML</option><option value="wick">wick</option><option value="close">close</option></select></F>
        <F label="Mode"><select value={params.entry_mode??""} onChange={e=>s("entry_mode",e.target.value||undefined)} style={sel}><option value="">YAML</option><option value="both">Both</option><option value="single choch">Single CHoCH</option><option value="double choch">Double CHoCH</option><option value="choch then bos">CHoCH→BOS</option><option value="bos in trend">BOS in Trend</option></select></F>
        <F label="Regime"><Tog checked={params.regime_enabled!==false} onChange={v=>s("regime_enabled",v)}/></F>
        <F label="Trail"><Tog checked={params.trailing_enabled!==false} onChange={v=>s("trailing_enabled",v)}/></F>
        <F label="BE"><Tog checked={params.be_enabled!==false} onChange={v=>s("be_enabled",v)}/></F>
        <F label="Pat. Age"><input type="number" value={params.max_pattern_age??""} onChange={e=>s("max_pattern_age",e.target.value?Number(e.target.value):undefined)} placeholder="YAML" style={inp} className="sl-input"/></F>
      </div>
    </div>
  </Card>;
}


/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */
export default function StrategyLab() {
  /* ── Core State ────────────────────────────── */
  const [tab, setTab] = useState("builder");
  const [inst, setInst] = useState("MGC");
  const [stratFile, setStratFile] = useState(null);

  /* ── Detail Views ──────────────────────────── */
  const [btDet, setBtDet] = useState(null);
  const [optDet, setOptDet] = useState(null);
  const [wfDet, setWfDet] = useState(null);
  // R7-5: scanDet removed with TF Scanner
  const [btCompare, setBtCompare] = useState(null);
  const [compareSelect, setCompareSelect] = useState(null);

  /* ── Backtest State ────────────────────────── */
  const [btStart, setBtStart] = useState("");
  const [barBtEnabled, setBarBtEnabled] = useState(false);
  const [btEnd, setBtEnd] = useState("");
  const [btTf, setBtTf] = useState("");
  const [running, setRunning] = useState(false);
  const [btOv, setBtOv] = useState({});
  const [showBtOv, setShowBtOv] = useState(false);

  /* ── Optimizer State ───────────────────────── */
  const [optMode, setOptMode] = useState("tuning");  // v4: single mode
  // ── Optimizer Run Config (Sprint B/C/D) ─────────────────────────────────
  const [runOptMode,    setRunOptMode]    = useState("standard");         // standard | wf_objective | pareto
  const [optProfile,    setOptProfile]    = useState("moderate");         // Sprint Profile-System: conservative | moderate | aggressive
  const [availableProfiles, setAvailableProfiles] = useState([]);         // loaded from /api/optimizer/profiles
  // Sprint H · Risk-Profile-System (separate vom optimizer_profile!)
  const [riskProfile,   setRiskProfile]   = useState("moderat");         // konservativ | moderat | aggressiv
  const [availableRiskProfiles, setAvailableRiskProfiles] = useState([]); // loaded from /api/risk/profiles
  const [wfObjSplit,    setWfObjSplit]    = useState(0.70);
  const [wfObjWindows,  setWfObjWindows]  = useState(3);
  const [wfObjMinTrades,setWfObjMinTrades]= useState(10);
  const [paretoAx1,     setParetoAx1]     = useState("net_pnl");
  const [paretoAx2,     setParetoAx2]     = useState("max_drawdown_pct");
  const [stabEnabled,   setStabEnabled]   = useState(false);
  const [stabTopN,      setStabTopN]      = useState(20);
  const [stabNeighbours,setStabNeighbours]= useState(6);
  const [stabPerturb,   setStabPerturb]   = useState(0.10);
  const [showRng, setShowRng] = useState(false); // trial analysis toggle
  const [showDiag, setShowDiag] = useState(false); // result-reorg #2: Suchraum-Diagnose, default zu
  const [showCand, setShowCand] = useState(false); // result-reorg #3: Top-Kandidaten, default zu
  // FIX R6-19: Option A (3 phases) vs Option B (2 phases) workflow selector
  const [optTrials, setOptTrials] = useState(1000);  // v4: sweet spot for 11 numeric + 5 toggle dims
  const [optObj, setOptObj] = useState("bucket_robust_score");  // v4: default primary objective
  const [optStart, setOptStart] = useState("2022-01-01");
  const [optEnd, setOptEnd] = useState("2026-04-01");
  const [optMinTrades, setOptMinTrades] = useState(100);  // v4: aligned with YAML goals.hard_constraints_is.min_trades
  const [optMaxDD, setOptMaxDD] = useState(12);  // v4: aligned with YAML goals.hard_constraints_is.max_drawdown_pct
  const [showConsensus, setShowConsensus] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyConfig, setVerifyConfig] = useState(null);
  const [bestSelection, setBestSelection] = useState("best_score");
  const [trialSort, setTrialSort] = useState({key:"score", dir:"desc"});
  const [consSel, setConsSel] = useState({});
  const [selTrial, setSelTrial] = useState(null);
  // v4: optInclTf state removed — TF-Search is gone, TFs are YAML-locked

  /* ── Walk-Forward State ────────────────────── */
  const [wfIsM, setWfIsM] = useState(6);
  const [wfOosM, setWfOosM] = useState(3);
  const [wfTrials, setWfTrials] = useState(200);
  const [wfMode, setWfMode] = useState("tuning");  // v4: only "tuning" mode supported
  // FIX WF-B2 v2: Optional date range. Default: auto-derive from full data (toggle off).
  // User toggles on to restrict WF to sub-period. Cleaner than always-visible empty inputs.
  const [wfUseCustomRange, setWfUseCustomRange] = useState(false);
  const [wfStart, setWfStart] = useState("2022-01-01");
  const [wfEnd, setWfEnd] = useState("2026-04-01");

  /* ── Scanner State ─────────────────────────── */
  const [scanMode, setScanMode] = useState("auto");
  const [scanStart, setScanStart] = useState("2022-01-01");
  const [scanEnd, setScanEnd] = useState("2026-04-01");
  const [scanETf, setScanETf] = useState("M5");
  const [scanCTf, setScanCTf] = useState("H1");
  const [scanSort, setScanSort] = useState({key:"score",dir:"desc"});

  /* ── AI Analyst State ──────────────────────── */
  const [aiQ, setAiQ] = useState("");
  const [aiRes, setAiRes] = useState(null);
  const [aiLd, setAiLd] = useState(false);
  const [prevTab, setPrevTab] = useState("builder");

  /* ── Diff Preview State ────────────────────── */
  const [diffPreview, setDiffPreview] = useState(null);

  /* ── Current YAML Config (for real Before/After diffs) ── */
  const [currentConfig, setCurrentConfig] = useState(null);
  useEffect(() => {
    if (!stratFile) { setCurrentConfig(null); return; }
    getApi(`/strategy-files/${encodeURIComponent(stratFile)}`).then(d => {
      if (d && !d.detail) {
        const cfg = d.config || (typeof d === "object" && !d.raw ? d : null);
        setCurrentConfig(cfg);
        // Sprint Profile-System: Use YAML's optimizer_profile as default (user can override)
        const yamlProfile = cfg?.goals?.optimizer_profile;
        if (yamlProfile && ["conservative","moderate","aggressive"].includes(yamlProfile)) {
          setOptProfile(yamlProfile);
        }
      }
    });
  }, [stratFile]);

  /* ── Sprint H · Risk-Profile-System: Load risk profiles from backend ── */
  useEffect(() => {
    getApi("/risk/profiles").then(d => {
      if (d && d.profiles) {
        setAvailableRiskProfiles(d.profiles);
        if (d.default && !riskProfile) setRiskProfile(d.default);
      }
    }).catch(() => {
      // Endpoint may not exist yet — silently fall back to hardcoded defaults
    });
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Sprint Profile-System: Load available profiles from backend ── */
  useEffect(() => {
    getApi("/optimizer/profiles").then(d => {
      if (d && d.profiles) {
        setAvailableProfiles(d.profiles);
        // Set default from backend (in case backend changes default)
        if (d.default && !optProfile) setOptProfile(d.default);
      }
    }).catch(() => {
      // Endpoint may not exist yet — silently fall back to hardcoded defaults
    });
  }, []);

  /* ── History Filters ───────────────────────── */
  const [hideOldRuns, setHideOldRuns] = useState(false);
  // R7s-1: Hide auto-baseline backtests by default — they're verification noise
  const [hideAutoBaselines, setHideAutoBaselines] = useState(true);
  const [hiddenRunIds, setHiddenRunIds] = useState(new Set());

  /* ── API Data ──────────────────────────────── */
  const {data:status} = useApi("/status", 5000);
  const {data:optSt} = useApi("/optimize/status", tab==="optimizer"?3000:0);
  const {data:wfSt} = useApi("/walkforward/status", tab==="walkforward"?5000:0);
  // R7-5: scanSt / scanH removed with TF Scanner
  const {data:stratFiles, refetch:refetchF} = useApi("/strategy-files", 30000);
  const {data:btH, refetch:refetchBt} = useApi("/results/backtests", tab==="backtest"?10000:0);
  const {data:optH, refetch:refetchOpt} = useApi("/results/optimizations", tab==="optimizer"?15000:0);
  const {data:wfH, refetch:refetchWf} = useApi("/results/walkforwards", tab==="walkforward"?15000:0);

  const strats = status?.strategies || {};
  const opt = ok(optSt) ? optSt : {};
  const wf = ok(wfSt) ? wfSt : {};
  const scan = {}; // R7-5: shim to keep downstream code that references `scan.*` from crashing
  const files = ok(stratFiles) ? (Array.isArray(stratFiles) ? stratFiles : stratFiles.files || []) : [];
  const btRuns = ok(btH) ? (Array.isArray(btH) ? btH : btH.runs || []) : [];
  const optRuns = ok(optH) ? (Array.isArray(optH) ? optH : optH.runs || []) : [];
  const wfRuns = ok(wfH) ? (Array.isArray(wfH) ? wfH : wfH.runs || []) : [];
  // R7-5: scanRuns removed — return empty array where referenced
  const scanRuns = [];
  // Stub refetchScan so deleteRun() can safely call it
  const refetchScan = () => {};

  // ── Phase F3 Layer 4 (Bug #74 Fix) ─────────────────────────────────
  // Client-side compositeScore-Berechnung wurde komplett ENTFERNT.
  // Backend (bot/api/scoring.py) ist Single-Source-of-Truth via
  //   - trial.composite_score (per-trial enrichment)
  //   - opt.best_trial_num (canonical Best-Trial from Hybrid Eligibility)
  //   - opt.best_eligibility (provenance info)
  // Vorher: 6. Composite-Implementation mit eigenen Weights (PnL 30/Sharpe 25/...)
  //   → führte zu Inkonsistenz mit Backend (PnL 45/DD 25/Sharpe 15/PF 10/WR 5).

  // Compute the "best trial" based on user's selected criterion.
  // Default "best_score" = Backend's canonical best_trial_num (Hybrid Eligibility).
  // Andere Cases (best_pnl, best_sharpe etc.) sind alternative Sichten — Dropdown erlaubt Override.
  const computedBest = (() => {
    const trials = opt.trial_history || [];
    if (trials.length === 0) return null;

    // Filter not-disqualified for all secondary metrics
    const validTrials = trials.filter(t => !t.is_disqualified);
    // Grade-A pool for "best_pnl_grade_a" and "lowest_dd"
    const gradeA = validTrials.filter(t => t.grade === "A");
    const pool = gradeA.length > 0 ? gradeA : validTrials;

    switch (bestSelection) {
      case "best_pnl":
        return [...validTrials].sort((a,b) => (b.net_pnl||0) - (a.net_pnl||0))[0];
      case "best_pnl_grade_a":
        return [...pool].sort((a,b) => (b.net_pnl||0) - (a.net_pnl||0))[0];
      case "best_sharpe":
        return [...validTrials].sort((a,b) => (b.sharpe||0) - (a.sharpe||0))[0];
      case "best_pf":
        return [...validTrials].sort((a,b) => (b.profit_factor||0) - (a.profit_factor||0))[0];
      case "lowest_dd":
        return [...pool].sort((a,b) => (a.max_drawdown||999) - (b.max_drawdown||999))[0];
      case "best_score":
      default: {
        // CANONICAL: Backend's canonical best_trial_num (Hybrid Eligibility-Layer).
        // Hard-Floor + Score-Eligibility (Top X% by Bayesian-Score) + Composite-Ranking.
        if (opt.best_trial_num != null) {
          const canonicalBest = trials.find(t => t.trial === opt.best_trial_num);
          if (canonicalBest) return canonicalBest;
        }
        // FALLBACK 1: Backend has composite_score field per-trial (legacy enrichment).
        // composite_score=-1 means Hard-Floor-Fail (excluded). >=0 means valid.
        const withScore = trials.filter(t => (t.composite_score ?? -1) >= 0);
        if (withScore.length > 0) {
          return [...withScore].sort((a,b) => (b.composite_score||0) - (a.composite_score||0))[0];
        }
        // FALLBACK 2: Bayesian score (oldest fallback, for legacy run-JSONs)
        return [...validTrials].sort((a,b) => (b.score||0) - (a.score||0))[0];
      }
    }
  })();
  const displayBestPnl = computedBest ? computedBest.net_pnl : opt.best_net_pnl;
  const displayBestGrade = computedBest ? computedBest.grade : opt.best_grade;
  // displayBestScore: Backend composite_score (canonical). Range 0-100.
  // Legacy alias: opt.best_composite_score (from get_status). Fallback null.
  const displayBestScore = computedBest?.composite_score ?? opt.best_composite_score ?? null;
  const displayBestParams = computedBest?.params || opt.best_params || {};

  /* ── Derived ───────────────────────────────── */
  const selFile = files.find(f => (f.filename||f.name) === stratFile);
  const realPipeline = selFile?.pipeline || { status:"created", history:[] };
  const pipelineStatus = realPipeline.status || "created";

  const clearDet = () => { setBtDet(null); setOptDet(null); setWfDet(null); setBtCompare(null); setCompareSelect(null); };
  const handleTabChange = t => { if (t === "ai") setPrevTab(tab); setTab(t); clearDet(); };

  /* ── Next Step Logic ───────────────────────── */
  // R7c-5: Tab order: Erstellen → Optimieren → Testen → Validieren.
  // Testen is for manual post-optimization verification (different date ranges, etc.);
  // Auto-Baseline-Backtest runs automatically after Apply Best/Consensus within Optimieren.
  const STEP_ORDER = ["builder","optimizer","backtest","walkforward"];
  const stepIdx = STEP_ORDER.indexOf(tab);
  const nextStep = stepIdx >= 0 && stepIdx < STEP_ORDER.length - 1 ? STEP_ORDER[stepIdx+1] : null;
  const nextLabel = { optimizer:"Optimieren", backtest:"Testen", walkforward:"Validieren" };

  const handleSelectStrategy = (filename, instrument) => {
    setStratFile(filename);
    if (instrument) setInst(instrument);
  };
  // T0-ARCH/T3-D2: Nach Onboarding ist der nächste Soll-Schritt OPTIMIEREN (nicht WF) —
  // Onboarding liefert nur Ranges, der Optimieren→WF→Apply-Zyklus findet die Werte.
  const handleOnboardToOptimize = (filename, instrument) => {
    setStratFile(filename);
    if (instrument) setInst(instrument);
    setTab("optimizer");
  };

  /* ── API Actions ───────────────────────────── */
  const runBt = async () => {
    setRunning(true);
    const b = { instrument:inst, ...btOv };
    if (stratFile) b.strategy_file = stratFile;
    if (btStart) b.start_date = btStart;
    if (btEnd) b.end_date = btEnd;
    if (btTf) b.entry_tf = btTf;
    // Sprint H · Risk-Profile-System: Position-Sizing
    if (riskProfile) b.risk_profile = riskProfile;
    // Sprint H Ebene 3 · Bar-Bootstrap opt-in (otherwise YAML default)
    if (barBtEnabled) b.bar_bootstrap_enabled = true;
    Object.keys(b).forEach(k => { if(b[k]===undefined||b[k]==="") delete b[k]; });
    await postApi("/backtest/run", b);
    setTimeout(() => { setRunning(false); refetchBt(); }, 3000);
  };

  const runOpt = () => postApi("/optimize/start", {
    instrument:inst, n_trials:optTrials, objective:optObj,
    start_date:optStart, end_date:optEnd, min_trades:optMinTrades,
    max_dd:optMaxDD,
    // Run Config (Sprint B/C/D) — overrides YAML goals
    optimization_mode: runOptMode,
    // Sprint Profile-System: profile selection (overrides YAML default)
    optimizer_profile: optProfile,
    // UI-Simplify Schritt 2: `mode:optMode` (hart "tuning") + `risk_profile:riskProfile`
    // (wirkt nur aufs Live-/Backtest-Sizing) wurden aus dem Optimizer-Payload entfernt —
    // sie waren hier wirkungslos. risk_profile bleibt im Backtest-Tab (runBt) aktiv.
    ...(runOptMode === "wf_objective" ? {
      wf_objective_config: {
        split_ratio:   wfObjSplit,
        n_oos_windows: wfObjWindows,
        oos_min_trades: wfObjMinTrades,
      },
    } : {}),
    ...(runOptMode === "pareto" ? {
      pareto_axes: [paretoAx1, paretoAx2],
    } : {}),
    ...(stabEnabled ? {
      stability_check: { enabled: true, top_n: stabTopN, n_neighbours: stabNeighbours, perturb_pct: stabPerturb },
    } : {}),
    ...(stratFile ? {strategy_file:stratFile} : {}),
  });
  const stopOpt = () => postApi("/optimize/stop");
  // FIX WF-B2 v2: Only send dates when user explicitly enabled custom range.
  // Otherwise backend auto-derives from full data bundle.
  const runWf = () => postApi("/walkforward/start", {
    instrument: inst,
    is_months: wfIsM,
    oos_months: wfOosM,
    n_trials: wfTrials,
    wf_mode: wfMode,
    ...(stratFile ? { strategy_file: stratFile } : {}),
    ...(wfUseCustomRange && wfStart ? { start_date: wfStart } : {}),
    ...(wfUseCustomRange && wfEnd ? { end_date: wfEnd } : {}),
  });
  const stopWf = () => postApi("/walkforward/stop");

  // R7-6: Auto-Baseline-Backtest after Apply Best / Apply Consensus.
  // Runs a full-period backtest on the just-written YAML, compares the result
  // to the expected PnL from the optimizer, and warns if they diverge by >10%.
  // This catches bugs where YAML-apply silently drops keys (as in the R7-1 bug).
  const runBaselineBacktest = async (expectedPnl, context = "Apply") => {
    if (!stratFile) return;
    if (expectedPnl === null || expectedPnl === undefined) return;
    try {
      toast(`🔬 Baseline-Backtest läuft... (${context} Verifikation)`, "info");
      const btBody = {
        instrument: inst,
        strategy_file: stratFile,
        start_date: optStart,
        end_date: optEnd,
        // R7s-1: Mark as auto-baseline so UI can show "Auto-Check" badge
        trigger: "auto_baseline",
        trigger_context: context,
      };
      const res = await postApi("/backtest/run", btBody);
      if (!res || res.detail) {
        toast(`⚠ Baseline-Backtest fehlgeschlagen: ${res?.detail || "Unknown"}`, "warn");
        return;
      }
      // Poll briefly for the latest backtest result
      setTimeout(async () => {
        try {
          // R7j-1: Use getApi helper instead of raw fetch — previously hardcoded
          // /api/results/backtests which goes to same-origin (Pages domain) not backend.
          const list = await getApi("/results/backtests");
          const runs = list?.runs || [];
          const latest = runs[0];
          if (!latest) {
            toast("⚠ Baseline-Backtest: kein Ergebnis gefunden", "warn");
            return;
          }
          const actualPnl = latest.net_pnl || latest.summary?.net_pnl || 0;
          const diff = Math.abs(actualPnl - expectedPnl);
          const diffPct = expectedPnl !== 0 ? (diff / Math.abs(expectedPnl)) * 100 : 0;
          if (diffPct > 10) {
            toast(`⚠ YAML-Drift: Optimizer sagte $${Math.round(expectedPnl).toLocaleString()}, Backtest ergibt $${Math.round(actualPnl).toLocaleString()} (${diffPct.toFixed(0)}% Abweichung)`, "warn");
          } else {
            toast(`✓ Verifiziert: Backtest reproduziert Optimizer-Ergebnis ($${Math.round(actualPnl).toLocaleString()}, Δ${diffPct.toFixed(1)}%)`, "success");
          }
          refetchBt?.();
        } catch (e) {
          toast(`⚠ Baseline-Check fehlgeschlagen: ${e.message}`, "warn");
        }
      }, 4000);
    } catch (e) {
      toast(`⚠ Baseline-Backtest Error: ${e.message}`, "warn");
    }
  };

  const runScan = () => postApi("/scan/start", { instrument:inst, mode:scanMode, start_date:scanStart, end_date:scanEnd, ...(stratFile?{strategy_file:stratFile}:{}), ...(scanMode==="manual"?{entry_tf:scanETf,context_tf:scanCTf}:{}) });
  const stopScan = () => postApi("/scan/stop");
  const runAi = async () => {
    if(!aiQ.trim()) return;
    setAiLd(true);
    const context = { instrument:inst, strategy_file:stratFile||undefined, pipeline_status:pipelineStatus, phase:optMode };
    const r = await postApi("/ai/analyze",{query:aiQ, ...context});
    setAiRes(r);
    setAiLd(false);
  };

  /* ── Apply Logic (with Diff Preview) ─────── */
  const applyTfToYaml = async (label) => {
    if (!stratFile) { toast("Zuerst eine Strategy-Datei wählen","warn"); return; }
    const parts = label.split("/");
    if (parts.length < 2) { toast("Ungültiges TF-Label","error"); return; }
    // Scanner findet Entry + Context (2-TF-Optimierung). Regime-TF bleibt aus YAML erhalten.
    const changes = [
      {key:"entry",newVal:parts[0],oldVal:null,section:"Timeframes"},
      {key:"context",newVal:parts[1],oldVal:null,section:"Timeframes"},
    ];
    const applyData = { entry: parts[0], context: parts[1] };
    // 3-teiliges Label wird defensiv auch unterstützt (Legacy-Scans / manuelle Eingabe)
    if (parts.length >= 3) {
      changes.push({key:"regime",newVal:parts[2],oldVal:null,section:"Timeframes"});
      applyData.regime = parts[2];
    }
    setDiffPreview({
      title:"Timeframes übernehmen",
      description: parts.length >= 3
        ? `Setzt ${label} in ${stratFile}.`
        : `Setzt Entry=${parts[0]} / Context=${parts[1]} in ${stratFile}. Regime-TF bleibt unverändert.`,
      stratFile,
      changes,
      onConfirm: async () => {
        const res = await postApi(`/strategy-files/${encodeURIComponent(stratFile)}/apply-results`, {type:"timeframes",data:applyData,description:`TF: ${label}`});
        if (res&&!res.detail) { toast(`TFs ${label} auf ${stratFile} angewandt`); refetchF?.(); setTab("backtest"); }
        else toast(res?.detail||"Error","error");
        setDiffPreview(null);
      },
    });
  };

  /* FIX R6-21: Apply TF Consensus — statistical decision instead of picking a single trial.
     Analyzes top 25% of scan results, finds dominant Entry-TF / Context-TF.
     Dominant = appears in ≥60% of top results.
     If Entry dominates but Context doesn't (→ TF-Search in Phase A decides), only Entry is fixed.
     This way the next Optimizer phase focuses on the statistically proven TF axis. */
  const applyTfConsensus = () => {
    if (!stratFile) { toast("Zuerst eine Strategy-Datei wählen","warn"); return; }
    const raw = scan.results || scan.scores || [];
    if (raw.length < 4) { toast("Zu wenige Scan-Ergebnisse für Consensus","warn"); return; }
    // Extract rows with label parseable
    const rows = raw.map(r => {
      const label = r.label || "";
      const [e, c] = label.split("/");
      const score = (r.composite_score || r.score || 0);
      const scoreNormed = typeof score === "number" && score <= 1 ? score * 100 : score;
      return { entry: e, context: c, score: scoreNormed, label };
    }).filter(r => r.entry && r.context);
    if (rows.length < 4) { toast("Labels nicht korrekt parsebar","warn"); return; }
    // Sort by score desc, take top quartile (min 5)
    const sorted = [...rows].sort((a,b) => b.score - a.score);
    const topN = Math.max(Math.ceil(sorted.length / 4), 5);
    const top = sorted.slice(0, topN);
    // Count frequencies
    const entryFreq = {};
    const contextFreq = {};
    top.forEach(r => {
      entryFreq[r.entry] = (entryFreq[r.entry] || 0) + 1;
      contextFreq[r.context] = (contextFreq[r.context] || 0) + 1;
    });
    // Find dominant (≥60% of top)
    const threshold = Math.ceil(topN * 0.6);
    const entryWinner = Object.entries(entryFreq).find(([, n]) => n >= threshold);
    const contextWinner = Object.entries(contextFreq).find(([, n]) => n >= threshold);
    // Build sorted frequency lists for UI
    const entrySorted = Object.entries(entryFreq).sort((a,b) => b[1] - a[1]);
    const contextSorted = Object.entries(contextFreq).sort((a,b) => b[1] - a[1]);
    // Build changes + summary
    const changes = [];
    const applyData = {};
    const insights = [];
    if (entryWinner) {
      changes.push({ key: "entry", newVal: entryWinner[0], oldVal: null, section: "Timeframes" });
      applyData.entry = entryWinner[0];
      insights.push(`✅ Entry-TF: ${entryWinner[0]} (${entryWinner[1]}/${topN} Top-Trials = ${Math.round(entryWinner[1]/topN*100)}%)`);
    } else {
      insights.push(`⚠️ Entry-TF: kein klarer Sieger (${entrySorted.slice(0,3).map(([tf,n]) => `${tf} ${Math.round(n/topN*100)}%`).join(", ")}) — Optimizer entscheidet`);
    }
    if (contextWinner) {
      changes.push({ key: "context", newVal: contextWinner[0], oldVal: null, section: "Timeframes" });
      applyData.context = contextWinner[0];
      insights.push(`✅ Context-TF: ${contextWinner[0]} (${contextWinner[1]}/${topN} Top-Trials = ${Math.round(contextWinner[1]/topN*100)}%)`);
    } else {
      insights.push(`ℹ️ Context-TF: verteilt (${contextSorted.slice(0,3).map(([tf,n]) => `${tf} ${Math.round(n/topN*100)}%`).join(", ")}) — bleibt offen für Phase A/1 mit TF-Search`);
    }
    if (changes.length === 0) {
      toast("Consensus: keine dominante TF-Achse (≥60%) gefunden — nutze Apply TF manuell","warn");
      return;
    }
    setDiffPreview({
      title: "TF-Consensus übernehmen",
      description: `Statistische Analyse der Top-${topN} Trials:\n\n${insights.join("\n")}\n\nNur statistisch dominante TFs (≥60%) werden fixiert. Offene werden in Phase A/1 mit TF-Search weiter optimiert.`,
      stratFile,
      changes,
      onConfirm: async () => {
        const res = await postApi(`/strategy-files/${encodeURIComponent(stratFile)}/apply-results`, {
          type: "timeframes", data: applyData,
          description: `TF-Consensus: ${Object.entries(applyData).map(([k,v]) => `${k}=${v}`).join(", ")}`,
        });
        if (res && !res.detail) {
          toast(`Consensus: ${changes.length} TF(s) fixiert — weiter mit Optimizer`);
          refetchF?.(); setTab("backtest");
        } else toast(res?.detail || "Error", "error");
        setDiffPreview(null);
      },
    });
  };

  const applyOptToYaml = async (params) => {
    if (!stratFile) { toast("Zuerst eine Strategy-Datei wählen","warn"); return; }

    // Warn if applying to a base YAML (not a clone/experiment)
    const isBaseYaml = stratFile && !stratFile.includes("__") && !stratFile.includes("experiments/") &&
      (stratFile.includes("_clean") || stratFile.includes("_moderat") || stratFile.includes("_aggressiv") ||
       stratFile.match(/v\d+_\d+/) || !stratFile.includes("_opt_"));
    if (isBaseYaml) {
      const confirmed = window.confirm(
        `⚠️ Basis-YAML Warnung\n\n` +
        `Du bist dabei "${stratFile}" zu überschreiben — das klingt nach einer Basis-YAML.\n\n` +
        `Empfehlung: Klone die YAML zuerst (Clone-Button oben), dann Apply auf die Kopie.\n\n` +
        `Trotzdem direkt auf die Basis schreiben?`
      );
      if (!confirmed) return;
    }

    const changes = buildDiffFromParams(params, currentConfig);
    const phaseLabel = OPTIMIZER_PHASES[optMode]?.label || optMode;
    setDiffPreview({
      title:`${phaseLabel} übernehmen`, description:`Schreibt beste Parameter in ${stratFile}.`, stratFile, changes,
      onConfirm: async () => {
        const changeDescs = Object.entries(params).flatMap(([sec,vals])=>Object.entries(vals||{}).map(([k,v])=>`${k}=${typeof v==="boolean"?v:typeof v==="number"?v.toFixed(2):v}`));
        const res = await postApi(`/strategy-files/${encodeURIComponent(stratFile)}/apply-results`, {type:"params",data:params,description:`Optimizer ${optMode}: ${changeDescs.slice(0,5).join(", ")}${changeDescs.length>5?` +${changeDescs.length-5} more`:""}`});
        if (res&&!res.detail) {
          toast(`${changeDescs.length} Parameter auf ${stratFile} angewandt`); refetchF?.();
          // R7-6: Trigger Auto-Baseline-Backtest to verify YAML reproduces the optimizer result
          const expectedPnl = computedBest?.net_pnl || opt.best_net_pnl;
          if (expectedPnl) runBaselineBacktest(expectedPnl, "Apply Best");
          // FIX R6-19 + R6-23: Progression logic, with TF-Search auto-disabled after Phase 1
          // v4: Single-mode — after tuning, user progresses to walkforward manually
          setTab("walkforward"); toast("Tuning done — starte Walk-Forward","info");
        } else toast(res?.detail||"Error","error");
        setDiffPreview(null);
      },
    });
  };

  /* ── Consensus Logic ───────────────────────── */
  // R7f-1: Optional sourceAnalysis lets the History-Card detail view pass its own
  // loaded run analysis instead of relying on the live opt.analysis.
  // consensusSource stores which analysis is active (live opt or historical run).
  const [consensusSource, setConsensusSource] = useState(null);

  const openConsensus = (sourceAnalysis = null, sourceMode = null, sourceBestParams = null, sourceBestPnl = null, sourceTrialHistory = null) => {
    const analysis = sourceAnalysis || opt.analysis;
    if (!analysis) {
      toast("Keine Consensus-Analyse verfügbar — erst Optimizer laufen lassen", "warn");
      return;
    }
    const trialHistorySrc = sourceTrialHistory || opt.trial_history || [];
    const items = buildConsensusItems(analysis, trialHistorySrc);
    if (items.fix.length === 0 && items.narrow.length === 0) {
      toast("Consensus leer — zu wenige valide Trials für statistische Analyse", "warn");
      return;
    }
    const sel = {};
    items.fix.forEach(it => sel[it.key]=true);
    items.narrow.forEach(it => sel[it.key]=true);
    setConsSel(sel); setShowConsensus(true);
    // R7l-1: Store full context: analysis + mode + best-trial params + pnl + trial_history (for lock-detection).
    setConsensusSource({
      analysis,
      mode: sourceMode,
      bestParams: sourceBestParams,
      bestPnl: sourceBestPnl,
      trial_history: sourceTrialHistory,
    });
    // FIX R6-18: Auto-scroll to Consensus panel.
    setTimeout(() => {
      const panel = Array.from(document.querySelectorAll("*")).find(
        e => e.textContent && e.textContent.startsWith("Consensus Review")
      );
      if (panel) panel.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const applyConsensus = async () => {
    if (!stratFile) { toast("Zuerst Strategy-Datei wählen","warn"); return; }
    // R7f-1: Use active consensusSource (historical run) or fall back to live opt.analysis
    const activeAnalysis = consensusSource?.analysis || opt.analysis;
    const trialHistorySrc = consensusSource?.trial_history || opt.trial_history || [];
    const items = buildConsensusItems(activeAnalysis, trialHistorySrc);
    const changes = buildDiffFromConsensus(items.fix,items.narrow,consSel,currentConfig);
    setDiffPreview({
      title:"Consensus übernehmen", description:"Fixiert sichere Entscheidungen und engt Ranges ein.", stratFile, changes,
      onConfirm: async () => {
        // R7l-2 Ansatz B: Apply Consensus writes complete, runnable YAML.
        // Step 1: Write Best-Trial's FULL params (numerics + TFs + structure + TPs + all).
        // This gives us a fully-configured baseline that reproduces the optimizer PnL.
        const bestParams = consensusSource?.bestParams || opt.best_params;
        if (bestParams && Object.keys(bestParams).length > 0) {
          const fullData = bestParamsToApplyData(bestParams);
          await postApi(
            `/strategy-files/${encodeURIComponent(stratFile)}/apply-results`,
            { type: "params", data: fullData, description: `Consensus baseline from best trial` }
          );
        }

        // Step 2: Overlay Consensus categorical fixes (may override best-trial categoricals
        // if they differ — e.g. best-trial has ema_mode=slope but consensus chose price_vs_ema).
        const structData = {};
        // R7k-1: Helper to merge sub-dicts instead of overwriting them.
        const merge = (section, fields) => {
          structData[section] = { ...(structData[section] || {}), ...fields };
        };
        items.fix.filter(it=>consSel[it.key]).forEach(it => {
          const v=it.value==="True"||it.value==="true"?true:it.value==="False"||it.value==="false"?false:it.value;
          if(it.key==="regime_enabled")merge("regime_filter", {enabled:v});
          else if(it.key==="ema_enabled")merge("ema_filter", {enabled:v});
          else if(it.key==="fvg_enabled")merge("fvg", {enabled:v});
          else if(it.key==="be_enabled")merge("break_even", {enabled:v});
          else if(it.key==="trail_mode")merge("trailing", {mode:v,enabled:v!=="none"});
          else if(it.key==="loss_scaling_enabled")merge("loss_scaling", {enabled:v});
          else if(it.key==="sl_mode")structData.sl_mode=v;
          else if(it.key==="entry_mode")structData.entry_mode=v;
          else if(it.key==="break_confirm")structData.break_confirm=v;
          else if(it.key==="regime_mode")merge("regime_filter", {mode:v});
          else if(it.key==="direction_filter")structData.direction_filter=v;
          else if(it.key==="require_htf_choch")structData.require_htf_choch=v;
          else if(it.key==="require_htf_trend")structData.require_htf_trend=v;
          else if(it.key==="ema_mode")merge("ema_filter", {mode:v});
          else if(it.key==="be_mode")merge("break_even", {mode:v});
          else if(it.key==="tp_mode")merge("take_profit", {mode:v});
          else if(it.key==="partial_trail_mode")merge("take_profit", {partial_trail_mode:v});
          // FIX K-40: Kernel reads `cfg.entry.fvg_proximity_enabled` (engine_fast.py:217),
          // not `cfg.fvg.proximity_enabled`. Previously written to the wrong section,
          // causing the feature to silently stay OFF even after the user explicitly enabled
          // it via Consensus. Route to the section the kernel actually reads.
          else if(it.key==="fvg_proximity_enabled")merge("entry", {fvg_proximity_enabled:v});
          else if(it.key==="regime_tf")structData.regime_tf=v;
          else if(it.key==="entry_tf")structData.entry_tf=v;
          else if(it.key==="context_tf")structData.context_tf=v;
        });
        if (Object.keys(structData).length>0) {
          const fixedNames=items.fix.filter(it=>consSel[it.key]).map(it=>`${it.key}=${it.value}`).join(", ");
          await postApi(`/strategy-files/${encodeURIComponent(stratFile)}/apply-results`,{type:"structure",data:structData,description:`Consensus: ${fixedNames}`});
        }
        const newRanges={};
        items.narrow.filter(it=>consSel[it.key]).forEach(it => {
          const k=it.key;
          if(k==="risk_pct"){newRanges.risk_pct_min=it.min;newRanges.risk_pct_max=it.max}
          else if(k==="adx_threshold"){newRanges.adx_min=Math.floor(it.min);newRanges.adx_max=Math.ceil(it.max)}
          else if(k==="atr_sma_length"){newRanges.sma_min=Math.floor(it.min);newRanges.sma_max=Math.ceil(it.max)}
          else if(k==="ctx_ema1_length"||k==="entry_ema1_length"||k==="ema_length"){
            newRanges.ema_min=Math.floor(it.min);newRanges.ema_max=Math.ceil(it.max);
          }
          else if(k==="rr1"||k==="tp1_rr"){newRanges.tp1_min=it.min;newRanges.tp1_max=it.max}
          else if(k==="rr2"||k==="tp2_rr"){newRanges.tp2_min=it.min;newRanges.tp2_max=it.max}
          else if(k==="rr3"||k==="tp3_rr"){newRanges.tp3_min=it.min;newRanges.tp3_max=it.max}
          else if(k==="rr4"||k==="tp4_rr"){newRanges.tp4_min=it.min;newRanges.tp4_max=it.max}
          else if(k==="be_trigger_rr"){newRanges.be_rr_min=it.min;newRanges.be_rr_max=it.max}
          else if(k==="trail_atr_mult"||k==="trailing_atr_mult"){newRanges.trail_mult_min=it.min;newRanges.trail_mult_max=it.max}
          else if(k==="trail_activation_rr"||k==="trailing_activation_rr"){newRanges.trail_act_min=it.min;newRanges.trail_act_max=it.max}
          else if(k==="context_swing_length"){newRanges.ctx_swing_min=Math.floor(it.min);newRanges.ctx_swing_max=Math.ceil(it.max)}
          else if(k==="entry_swing_length"){newRanges.entry_swing_min=Math.floor(it.min);newRanges.entry_swing_max=Math.ceil(it.max)}
          else if(k==="max_pattern_age"){newRanges.pattern_age_min=Math.floor(it.min);newRanges.pattern_age_max=Math.ceil(it.max)}
        });
        setShowConsensus(false); refetchF?.();
        const nFixed=items.fix.filter(it=>consSel[it.key]).length;
        const nNarrow=items.narrow.filter(it=>consSel[it.key]).length;
        toast(`Consensus: ${nFixed} fixiert, ${nNarrow} Ranges eingeengt, Best-Trial-Numerics geschrieben`);
        // R7l-2: Baseline expectation now uses best_net_pnl (not top_avg_pnl) because
        // the YAML now reflects the exact best-trial params, not the consensus average.
        const expectedPnl = consensusSource?.bestPnl
          || computedBest?.net_pnl
          || opt.best_net_pnl;
        if (expectedPnl) runBaselineBacktest(expectedPnl, "Apply Consensus");
        // v4: single-flow — no multi-phase progression
        setConsensusSource(null);
        setDiffPreview(null);
      },
    });
  };

  const handleCompare = id => { if(!compareSelect) setCompareSelect(id); else if(compareSelect!==id){setBtCompare([compareSelect,id]);setCompareSelect(null);} };

  /* ── Filter runs by strategy file ──────────── */
  const filterRuns = (runs, opts = {}) => {
    let r = runs;
    if (hideOldRuns && stratFile) r = r.filter(run => (run.strategy_file||"") === stratFile || (run.instrument||"") === inst);
    r = r.filter(run => !hiddenRunIds.has(run.id));
    // R7s-1: Optionally hide auto-baseline backtests (system-verification noise)
    if (opts.hideAutoBaselines) r = r.filter(run => run.trigger !== "auto_baseline");
    return r;
  };
  const hideRun = (id) => { if(id) setHiddenRunIds(prev => { const n = new Set(prev); n.add(id); return n; }); toast("Run ausgeblendet"); };
  const deleteRun = (id, type) => {
    if (!id) return;
    showModal("Run löschen?", `Run #${id} wird permanent aus Datenbank und Dateisystem entfernt. Fortfahren?`, async () => {
      try {
        const res = await fetch(`${API_BASE}/results/${type}/${id}`, {method:"DELETE"});
        if (res.ok) {
          // FIX R6-22: Real backend delete, parse response for details
          let body = null;
          try { body = await res.json(); } catch {}
          const files = body?.deleted_files ?? 0;
          const row = body?.deleted_row ?? 0;
          toast(`Run #${id} gelöscht (DB: ${row}, Files: ${files})`);
          refetchBt(); refetchOpt(); refetchWf(); refetchScan();
        } else if (res.status === 404) {
          // Already gone on backend — just remove from UI
          hideRun(id);
          toast(`Run #${id} war nicht mehr im Backend — ausgeblendet`, "info");
        } else {
          // 4xx/5xx from backend — fall back to soft-delete
          const detail = await res.text().catch(() => "");
          hideRun(id);
          toast(`Delete fehlgeschlagen (${res.status}) — Run ausgeblendet${detail ? ": " + detail.slice(0, 80) : ""}`, "warn");
        }
      } catch {
        hideRun(id);
        toast("API nicht erreichbar — Run ausgeblendet", "warn");
      }
    });
  };


  /* ═══ RENDER ═══════════════════════════════════ */
  return <>
    {/* ── Diff Preview Modal ─────────────────── */}
    {diffPreview && <DiffPreview title={diffPreview.title} description={diffPreview.description} stratFile={diffPreview.stratFile} changes={diffPreview.changes} onConfirm={diffPreview.onConfirm} onCancel={()=>setDiffPreview(null)}/>}

    {/* ── Strategy Context Bar ───────────────── */}
    <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16, padding:"14px 20px", borderRadius:12, background:theme.white, border:`1px solid ${theme.divider}`, boxShadow:theme.shadow1, flexWrap:"wrap" }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={theme.text3} strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><polyline points="14 2 14 8 20 8"/></svg>
      <select value={stratFile||""} onChange={e=>{const name=e.target.value;if(name){setStratFile(name);const f=files.find(f=>(f.filename||f.name)===name);if(f?.instrument)setInst(f.instrument);}else setStratFile(null);}} style={{...sel,width:"auto",minWidth:200,fontSize:14,fontWeight:700}}>
        <option value="">Strategie wählen…</option>
        <optgroup label="📁 Strategien">
          {files.filter(f=>{const s=typeof f==="object"?f.status:"test";return s!=="archived" && !f.is_experiment;}).map(f=>{const n=typeof f==="string"?f:f.filename||f.name||"";const i2=typeof f==="object"?f.instrument:"";const pl=typeof f==="object"?(f.pipeline?.status||"created"):"";return<option key={n} value={n}>{i2?`${i2} — `:""}{n} {pl?`(${pl.replace("_"," ")})`:""}</option>;})}
        </optgroup>
        {files.some(f=>f.is_experiment && f.status!=="archived") && (
          <optgroup label="🧪 Experimente">
            {files.filter(f=>f.is_experiment && f.status!=="archived").map(f=>{const n=f.filename;const i2=f.instrument||"";const es=f.experiment_status||"pending";return<option key={n} value={n}>{i2?`${i2} — `:""}{f.experiment_name||n} [{es}]</option>;})}
          </optgroup>
        )}
      </select>
      {stratFile && selFile && <>
        <Pill bg={theme.blueLight} color={theme.blueText}><span style={{fontSize:12,fontWeight:800}}>{selFile.instrument||inst}</span></Pill>
        {selFile.timeframes && <span style={{fontSize:13,fontWeight:600,fontFamily:fonts.mono,color:theme.text2}}>{selFile.timeframes.entry}/{selFile.timeframes.context}/{selFile.timeframes.regime}</span>}
        <Pill bg={pipelineStatus==="validated"?theme.greenLight:pipelineStatus==="optimized"?theme.amberLight:theme.tint} color={pipelineStatus==="validated"?theme.greenText:pipelineStatus==="optimized"?theme.amberText:theme.text4}><span style={{fontSize:11,fontWeight:700}}>{pipelineStatus.replace("_"," ")}</span></Pill>
        {realPipeline.history && realPipeline.history.length > 0 && <span style={{fontSize:11,color:theme.text3,fontFamily:fonts.mono,marginLeft:"auto"}} title={realPipeline.history.map(h=>`${h.step}: ${h.description} (${fDateShort(h.timestamp)})`).join("\n")}>
          {realPipeline.history.length} Steps · {fDateShort(realPipeline.history[realPipeline.history.length-1]?.timestamp)}
        </span>}
      </>}
      {!stratFile && <span style={{fontSize:13,fontWeight:600,color:theme.amberText}}>Keine Strategie gewählt — wähle oder erstelle eine im Builder</span>}
      <div style={{marginLeft:stratFile?"0":"auto",display:"flex",alignItems:"center",gap:6}}>
        <Tog checked={hideOldRuns} onChange={setHideOldRuns} label="Nur aktuelle"/>
      </div>
    </div>

    {/* ── Strategy Card Strip ────────────────── */}
    {files.filter(f=>typeof f==="object"&&f.status!=="archived").length > 0 && <div style={{
      display:"flex", gap:8, marginBottom:16, overflowX:"auto", paddingBottom:4,
    }}>
      {files.filter(f=>typeof f==="object"&&f.status!=="archived").map((f,i) => {
        const n = f.filename||f.name||"";
        const isActive = stratFile === n;
        const isExp = !!f.is_experiment;
        const pl = f.pipeline?.status || "created";
        const plC = {created:theme.text5,tf_scanned:theme.blue,structure_tested:theme.purple,optimized:theme.amber,validated:theme.green}[pl]||theme.text5;
        // Experiment status color
        const expStatusColors = {
          pending: theme.text5,
          optimized: theme.blue,
          validated: theme.green,
          promoted: theme.green,
          archived: theme.text5,
        };
        const expStatus = f.experiment_status || "pending";
        const expC = expStatusColors[expStatus] || theme.text5;
        const borderColor = isActive ? theme.blue : (isExp ? "#f59e0b" : theme.divider);
        const activeBg = isActive ? (isExp ? "rgba(245,158,11,0.05)" : "rgba(37,99,235,0.04)") : theme.white;
        return <div key={i} onClick={() => { setStratFile(n); if(f.instrument) setInst(f.instrument); }} style={{
          padding:"10px 16px", borderRadius:10, cursor:"pointer",
          border:`2px ${isExp?"dashed":"solid"} ${borderColor}`,
          background:activeBg,
          minWidth:160, flexShrink:0, transition:"all 0.15s",
          boxShadow:isActive?theme.shadow2:"none",
          position:"relative",
        }}>
          {isExp && (
            <div style={{
              position:"absolute", top:-8, right:8,
              background:"#f59e0b", color:"#fff",
              fontSize:9, fontWeight:800, padding:"2px 8px",
              borderRadius:8, letterSpacing:0.5,
            }}>🧪 EXPERIMENT</div>
          )}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
            <span style={{ fontSize:15, fontWeight:isActive?900:700, fontFamily:fonts.mono, color:isActive?theme.blueText:theme.text }}>{f.instrument||"?"}</span>
            <span style={{ width:8, height:8, borderRadius:4, background:isExp?expC:plC }}/>
          </div>
          <div style={{ fontSize:11, color:theme.text3, fontFamily:fonts.mono, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
            {isExp ? (f.experiment_name || n.replace("experiments/","")) : n}
          </div>
          <div style={{ fontSize:10, color:theme.text4, marginTop:3 }}>
            {isExp
              ? <>{expStatus}{f.n_changes ? ` · ${f.n_changes} Änd.` : ""}{f.derived_from_run ? ` · ${f.derived_from_run}` : ""}</>
              : <>{pl.replace("_"," ")} · {f.status||"test"}</>
            }
          </div>
        </div>;
      })}
      <div onClick={() => { setStratFile(null); handleTabChange("builder"); }} style={{
        padding:"10px 16px", borderRadius:10, cursor:"pointer",
        border:`2px dashed ${theme.border}`, background:"transparent",
        minWidth:110, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center",
      }}>
        <span style={{ fontSize:14, fontWeight:700, color:theme.text4 }}>+ Neue</span>
      </div>
    </div>}

    {/* ── Pipeline Stepper ───────────────────── */}
    <PipelineStepper activeTab={tab} pipelineStatus={pipelineStatus} onTabChange={handleTabChange} stratFile={stratFile}/>

    {/* ═══ BUILDER ═══ */}
    {tab==="builder" && <>
      <BuilderTab files={files} onRefetch={refetchF} onSelectStrategy={handleSelectStrategy} onOptimize={handleOnboardToOptimize} selectedFile={stratFile}/>
      {nextStep && <div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}><Button primary className="sl-btn" onClick={()=>handleTabChange(nextStep)}>Weiter: {nextLabel[nextStep]} →</Button></div>}
    </>}
    {/* ═══ BACKTEST ═══ */}
    {tab==="backtest" && (btCompare?<BtCompare ids={btCompare} onClose={()=>{setBtCompare(null);setCompareSelect(null);}}/>:btDet?<BtDetail runId={btDet} onClose={()=>setBtDet(null)}/>:<>
      {/* Seed-Rest-Warnung: ein needs_optimize-Artefakt trägt feste Seed-Werte; ein Backtest
          nutzt DIE (Engine liest nur feste Werte, nie goals.ranges) — NICHT instrument-eigene.
          Status aus der echten cfg (_meta.status + pipeline.status), kein Hardcode. Rein
          additiv/read-only: ändert NICHTS am Backtest-Verhalten (Button bleibt aktiv). */}
      {(() => {
        const meta = currentConfig?._meta?.status;
        const pl = currentConfig?.pipeline?.status;
        const APPLIED = ["optimized","validated","walkforwarded","cross_validated"];
        if (meta !== "needs_optimize" || APPLIED.includes(pl)) return null;
        return <div style={{padding:"12px 16px",borderRadius:8,background:"rgba(217,119,6,0.10)",border:`1px solid ${theme.amber||"#d97706"}`,fontSize:13,color:theme.amber||"#b45309",marginBottom:16,lineHeight:1.6}}>
          ⚠️ <b>Seed-Reste — noch nicht instrument-eigen optimiert.</b> Dieses Artefakt trägt feste Werte (z.B. TP1 RR, SL, Trails) noch aus dem Seed. Ein Backtest nutzt diese <b>Seed-Werte</b>, NICHT instrument-eigene — das Ergebnis ist „Seed-Strategie auf {inst||"Instrument"}-Daten". Erst <b>Optimieren → Apply</b> schreibt instrument-eigene Werte ins YAML. (Onboarding steckt nur Suchräume ab — §0.) <span style={{opacity:0.8}}>Status: <code>_meta.status:{meta}</code>{pl?<> · <code>pipeline:{pl}</code></>:null}</span>
        </div>;
      })()}
      <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:16,flexWrap:"wrap"}}>
        <F label="Start"><input type="date" value={btStart} onChange={e=>setBtStart(e.target.value)} style={{...inp,width:130}}/></F>
        <F label="End"><input type="date" value={btEnd} onChange={e=>setBtEnd(e.target.value)} style={{...inp,width:130}}/></F>
        <F label="Entry TF"><select value={btTf} onChange={e=>setBtTf(e.target.value)} style={{...sel,width:65}}><option value="">Auto</option>{TFS.map(t=><option key={t}>{t}</option>)}</select></F>
        <F label="Bar-Bootstrap">
          <label style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",borderRadius:6,border:`1px solid ${theme.divider}`,background:barBtEnabled?theme.tint:"transparent",cursor:"pointer",fontSize:11,color:theme.text2,whiteSpace:"nowrap"}} title="Sprint H Ebene 3: Re-runt Strategy auf 20 synthetischen OHLC-Märkten (+~30s)">
            <input type="checkbox" checked={barBtEnabled} onChange={e=>setBarBtEnabled(e.target.checked)} style={{margin:0,cursor:"pointer"}}/>
            <span>📊 Bar-Bootstrap (+30s)</span>
          </label>
        </F>
        <Button primary onClick={runBt} disabled={running}>{running?"Running…":"Run Backtest"}</Button>

        {/* Sprint H · Risk-Profile-Selector im Backtest-Tab */}
        <div style={{width:"100%", marginTop:14, padding:"12px 14px", borderRadius:10, background:theme.tint, border:`1px solid ${theme.divider}`}}>
          <div style={{fontSize:11, fontWeight:700, color:theme.text2, marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
            <span>🛡 Risk-Profile</span>
            <span style={{fontSize:10, fontWeight:500, color:theme.text4, fontStyle:"italic"}}>Position-Sizing für diesen Backtest</span>
          </div>
          <div style={{display:"flex", gap:8}}>
            {[
              {key:"konservativ", label:"Konservativ", color:theme.blueText,  emoji:"🛡"},
              {key:"moderat",     label:"Moderat",     color:theme.greenText, emoji:"⚖", badge:"Default"},
              {key:"aggressiv",   label:"Aggressiv",   color:theme.amberText||"#d97706", emoji:"🚀"},
            ].map(({key,label,color,emoji,badge}) => {
              const active = riskProfile === key;
              const profDetails = (availableRiskProfiles || []).find(p => p.name === key);
              const sub = profDetails ? `${profDetails.risk_pct}% · max ${profDetails.max_contracts} contracts` : "";
              const tooltip = profDetails ? profDetails.description : "";
              return <button key={key} onClick={()=>setRiskProfile(key)} title={tooltip} style={{
                flex:1, padding:"8px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
                border:`2px solid ${active ? color : theme.border}`,
                background: active ? theme.white : theme.white,
              }}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <span style={{fontSize:12, fontWeight:700, color: active ? color : theme.text}}>
                    <span style={{marginRight:4}}>{emoji}</span>{label}
                  </span>
                  {badge && <span style={{fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3, background: active ? color : theme.border, color: active ? "#fff" : theme.text3}}>{badge}</span>}
                </div>
                <div style={{fontSize:10, color: active ? color : theme.text3, marginTop:2, fontFamily:fonts.mono}}>{sub}</div>
              </button>;
            })}
          </div>
        </div>
        <ElapsedTimer running={running}/>
      </div>
      <BtParamPanel params={btOv} setParams={setBtOv} show={showBtOv} onToggle={()=>setShowBtOv(!showBtOv)}/>
      {compareSelect&&<div style={{padding:"8px 14px",borderRadius:8,background:theme.blueLight,color:theme.blueText,fontSize:11,marginBottom:14}}>Run #{compareSelect} ausgewählt — klicke "vs" auf einem zweiten Run. <button onClick={()=>setCompareSelect(null)} style={{background:"none",border:"none",color:theme.blueText,cursor:"pointer",textDecoration:"underline",fontSize:11}}>Abbrechen</button></div>}
      {/* R7s-1: Filter-Toggle für Auto-Baselines */}
      {(() => {
        const totalRuns = filterRuns(btRuns).length;
        const visibleRuns = filterRuns(btRuns, {hideAutoBaselines}).length;
        const hiddenCount = totalRuns - visibleRuns;
        return <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12,fontSize:11,color:theme.text3}}>
          <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer"}}>
            <input type="checkbox" checked={hideAutoBaselines} onChange={e=>setHideAutoBaselines(e.target.checked)} style={{cursor:"pointer"}}/>
            <span>Auto-Baseline-Checks ausblenden {hiddenCount>0 && <span style={{color:theme.amberText,fontWeight:700}}>({hiddenCount} versteckt)</span>}</span>
          </label>
          <span style={{fontSize:10,color:theme.text4,fontStyle:"italic"}}>
            🔬 Auto-Checks sind Verifikations-Backtests die automatisch nach Apply Consensus/Best laufen.
          </span>
        </div>;
      })()}
      {filterRuns(btRuns,{hideAutoBaselines}).length>0?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>{filterRuns(btRuns,{hideAutoBaselines}).map((r,i)=><RunCard key={r.id||i} run={r} onClick={r.id?()=>setBtDet(r.id):undefined} onCompare={handleCompare} onDelete={id=>deleteRun(id,"backtests")} onHide={hideRun}/>)}</div>:<Empty icon="chart" title="Keine Backtests" sub="Teste die aktuelle Konfiguration." btn="Run Backtest" onClick={runBt}/>}
      {nextStep&&<div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}><Button primary className="sl-btn" onClick={()=>handleTabChange(nextStep)}>Weiter: {nextLabel[nextStep]} →</Button></div>}
    </>)}


    {/* ═══ OPTIMIZER ═══ */}
    {/* R7f-4: Outer fragment so Consensus Panel can render above both OptDetail and main view */}
    {tab==="optimizer" && <>
      {/* Consensus Panel — rendered first so it's visible in both list and detail views */}
      {showConsensus&&(consensusSource?.analysis||opt.analysis)&&(()=>{const _a=consensusSource?.analysis||opt.analysis;const _th=consensusSource?.trial_history||opt.trial_history||[];const items=buildConsensusItems(_a,_th);return<Card style={{marginBottom:16,border:`2px solid ${theme.blue}`}} className="sl-fadein">
        <CardHeader title="Consensus Review" sub={consensusSource?`Aus Optimization #${consensusSource.runId||"?"} (${consensusSource.mode||"?"})`:"Prüfe und wähle was übernommen wird"} right={<div style={{display:"flex",gap:6}}><Button primary onClick={applyConsensus}>Übernehmen</Button><Button onClick={()=>{setShowConsensus(false);setConsensusSource(null);}}>Abbrechen</Button></div>}/>
        <div style={{padding:"14px 20px"}}>
          {items.fix.length>0&&<div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:theme.greenText,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,borderRadius:"50%",background:theme.green}}/> Fixieren (≥80% Confidence) → wird in YAML geschrieben</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:6}}>
              {items.fix.map(it=><label key={it.key} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:6,background:consSel[it.key]?theme.greenLight:theme.tint,border:`1px solid ${consSel[it.key]?"rgba(22,163,74,0.15)":theme.divider}`,cursor:"pointer"}}>
                <input type="checkbox" checked={!!consSel[it.key]} onChange={e=>setConsSel(p=>({...p,[it.key]:e.target.checked}))} style={{accentColor:theme.green}}/>
                <div><div style={{fontSize:11,fontWeight:700}}>{it.key}</div><div style={{fontSize:12,fontWeight:800,fontFamily:fonts.mono,color:theme.greenText}}>{String(it.value)}</div><div style={{fontSize:9,color:theme.text3}}>{it.confidence}%</div></div>
              </label>)}
            </div>
          </div>}
          {items.narrow.length>0&&<div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:theme.blueText,marginBottom:8,display:"flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,borderRadius:"50%",background:theme.blue}}/> Ranges einengen (P10–P90)</div>
            <div style={{overflow:"auto",maxHeight:250}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`1px solid ${theme.divider}`}}>{["","Parameter","Top Mean","Bottom Mean","Neue Range"].map(h=><th key={h} style={{padding:"5px 8px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"left"}}>{h}</th>)}</tr></thead><tbody>
              {items.narrow.map(it=><tr key={it.key} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`,opacity:consSel[it.key]?1:0.4}}>
                <td style={{padding:"4px 8px"}}><input type="checkbox" checked={!!consSel[it.key]} onChange={e=>setConsSel(p=>({...p,[it.key]:e.target.checked}))} style={{accentColor:theme.blue}}/></td>
                <td style={{padding:"4px 8px",fontSize:11,fontWeight:600}}>{it.key}</td>
                <td style={{padding:"4px 8px",fontSize:11,fontFamily:fonts.mono,color:theme.greenText}}>{it.topMean}</td>
                <td style={{padding:"4px 8px",fontSize:11,fontFamily:fonts.mono,color:theme.redText}}>{it.bottomMean}</td>
                <td style={{padding:"4px 8px",fontSize:11,fontFamily:fonts.mono,fontWeight:700,color:theme.blueText}}>[{it.min} – {it.max}]</td>
              </tr>)}
            </tbody></table></div>
          </div>}
          {items.open.length>0&&<div>
            <div style={{fontSize:11,fontWeight:700,color:theme.text3,marginBottom:6,display:"flex",alignItems:"center",gap:6}}><span style={{width:10,height:10,borderRadius:"50%",background:theme.text4}}/> Offen lassen (&lt;80%)</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{items.open.map(it=><div key={it.key} style={{padding:"5px 10px",borderRadius:6,background:theme.tint,fontSize:10.5}}><span style={{fontWeight:600}}>{it.key}</span>: {String(it.value)} <span style={{color:theme.text4}}>({it.confidence}%)</span></div>)}</div>
          </div>}
        </div>
      </Card>;})()}
      {optDet?<OptDetail runId={optDet} onClose={()=>setOptDet(null)} onApply={stratFile?applyOptToYaml:null} onApplyConsensus={openConsensus} stratFile={stratFile}/>:<>
      {/* ── Trials + Objective + Start ── */}
      <div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:14,flexWrap:"wrap"}}>
        <div style={{display:"flex",flexDirection:"column",gap:2}}>
          <F label="Trials"><input type="number" value={optTrials} onChange={e=>setOptTrials(Number(e.target.value))} min={10} max={5000} step={10} style={{...inp,width:90}}/></F>
          {(() => {
            const p = OPTIMIZER_PHASES[optMode];
            if (!p || !p.trials_min) return null;
            const v = optTrials;
            let color = theme.text4, icon = "";
            if (v < p.trials_min) { color = theme.redText; icon = "⚠ zu wenig"; }
            else if (v >= p.trials_min && v < p.trials_sweet) { color = theme.amberText; icon = "🟡 unter Sweet-Spot"; }
            else if (v >= p.trials_sweet && v <= p.trials_max) { color = theme.greenText; icon = "✓ Sweet-Spot"; }
            else if (v > p.trials_max) { color = theme.amberText; icon = "⚠ Diminishing Returns"; }
            return <div style={{fontSize:10,color,fontFamily:fonts.mono,paddingLeft:4}}>
              min {p.trials_min} · sweet {p.trials_sweet} · max {p.trials_max} {icon && <span style={{fontWeight:700,marginLeft:4}}>{icon}</span>}
            </div>;
          })()}
        </div>
        <F label="Objective">
          <div style={{
            ...sel, width:"auto", display:"flex", alignItems:"center", gap:6,
            background: runOptMode==="wf_objective" ? theme.blueLight : runOptMode==="pareto" ? "rgba(217,119,6,0.08)" : theme.greenLight,
            color: runOptMode==="wf_objective" ? theme.blueText : runOptMode==="pareto" ? (theme.amberText||"#d97706") : theme.greenText,
            fontWeight:700, cursor:"default", userSelect:"none",
          }}>
            <span>{runOptMode==="wf_objective" ? "🎯" : runOptMode==="pareto" ? "⚖️" : (optObj==="oos_consistent_pnl" ? "💰" : "⭐")}</span>
            <span>{runOptMode==="wf_objective" ? "OOS Score (avg)" : runOptMode==="pareto" ? `${paretoAx1} / ${paretoAx2}` : (optObj==="oos_consistent_pnl" ? "PnL-Max" : "Bucket Robust Score")}</span>
          </div>
        </F>
        {opt.running?<Button danger onClick={stopOpt}>Stop</Button>:<Button primary onClick={runOpt}>Start Optimizer</Button>}
      </div>

      {/* ── Run Config Panel ── */}
      {!opt.running && <div style={{marginBottom:16, padding:"14px 16px", borderRadius:10, background:theme.tint, border:`1px solid ${theme.divider}`}}>
        <div style={{fontSize:11, fontWeight:800, letterSpacing:"1.2px", textTransform:"uppercase", color:theme.text3, marginBottom:12}}>Run Konfiguration</div>

        {/* Objective (Score-Funktion) selector — getrennte Achse vom Modus.
            Steuert get_score_function im Backend (objective:optObj im /optimize/start-Payload). */}
        <div style={{fontSize:12, fontWeight:700, color:theme.text2, marginBottom:8, display:"flex", alignItems:"center", gap:6}}>
          <span>Such-Ziel (Objective)</span>
          <span style={{fontSize:10, fontWeight:500, color:theme.text4, fontStyle:"italic"}}>wonach Optuna sucht — unabhängig vom Modus</span>
        </div>
        <div style={{display:"flex", gap:8, marginBottom:6}}>
          {[
            {key:"bucket_robust_score", label:"Bucket Robust Score", sub:"bestes Verhältnis PnL/Drawdown (Calmar)", color:theme.greenText, emoji:"⭐", badge:"Default"},
            {key:"oos_consistent_pnl",  label:"PnL-Max",             sub:"höchster PnL, DD nur als harte Grenze (MAX DD %)", color:theme.amberText||"#d97706", emoji:"💰"},
          ].map(({key,label,sub,color,emoji,badge}) => {
            const active = optObj === key;
            return <button key={key} onClick={()=>setOptObj(key)} style={{
              flex:1, padding:"8px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
              border:`2px solid ${active ? color : theme.border}`,
              background: active ? theme.white : theme.tint,
            }}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <span style={{fontSize:12, fontWeight:700, color: active ? color : theme.text}}>
                  <span style={{marginRight:4}}>{emoji}</span>{label}
                </span>
                {badge && <span style={{fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3, background: active ? color : theme.border, color: active ? "#fff" : theme.text3}}>{badge}</span>}
              </div>
              <div style={{fontSize:10, color: active ? color : theme.text3, marginTop:2}}>{sub}</div>
            </button>;
          })}
        </div>
        <div style={{fontSize:10, color:theme.text4, marginBottom:16, fontStyle:"italic"}}>
          Gilt für den IS-Score (Standard-Modus). WF-Objective rankt final nach OOS, Pareto nach den gewählten Achsen.
        </div>

        {/* Profile selector — Sprint Profile-System */}
        <div style={{fontSize:12, fontWeight:700, color:theme.text2, marginBottom:8}}>Optimizer-Profile</div>
        <div style={{display:"flex", gap:8, marginBottom:16}}>
          {[
            {key:"conservative", label:"Konservativ", sub:"DD-Gewichtung hoch · max. Stabilität", color:theme.blueText,  emoji:"🛡️"},
            {key:"moderate",     label:"Moderat",     sub:"DD-Gewichtung mittel · balanced",     color:theme.greenText, emoji:"⚖️", badge:"Default"},
            {key:"aggressive",   label:"Aggressiv",   sub:"DD-Gewichtung niedrig · PnL-Maximierung", color:theme.amberText||"#d97706", emoji:"🚀"},
          ].map(({key,label,sub,color,emoji,badge}) => {
            const active = optProfile === key;
            const profDetails = (availableProfiles || []).find(p => p.name === key);
            const tooltip = profDetails ? `${profDetails.description}\n\nGeeignet für: ${profDetails.suitable_for}` : "";
            return <button key={key} onClick={()=>setOptProfile(key)} title={tooltip} style={{
              flex:1, padding:"8px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
              border:`2px solid ${active ? color : theme.border}`,
              background: active ? theme.white : theme.tint,
            }}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <span style={{fontSize:12, fontWeight:700, color: active ? color : theme.text}}>
                  <span style={{marginRight:4}}>{emoji}</span>{label}
                </span>
                {badge && <span style={{fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3, background: active ? color : theme.border, color: active ? "#fff" : theme.text3}}>{badge}</span>}
              </div>
              <div style={{fontSize:10, color: active ? color : theme.text3, marginTop:2}}>{sub}</div>
            </button>;
          })}
        </div>

        {/* UI-Simplify Schritt 3 (a): Profil-DD-Stufe entfernt. Der Profil-„hard_floor" für DD
            ist im Backend auf 25% Backstop neutralisiert (scoring_profiles.py); die DD-Disziplin
            kommt jetzt allein vom EINEN „Max Drawdown %"-Feld unten (Lauf-Gate → is_disqualified,
            das auch die Best-Trial-Auswahl bindet). Das Profil steuert nur noch die Composite-
            Gewichtung. Frühere „Zwei DD-Stufen"-Erklärung + hardcodiertes PROFILE_DD entfernt. */}
        <div style={{marginBottom:16, padding:"8px 11px", borderRadius:8, background:theme.tint, border:`1px solid ${theme.divider}`, fontSize:11, color:theme.text3, lineHeight:1.5}}>
          <b style={{color:theme.text2}}>DD-Steuerung:</b> „Max Drawdown %" (unten) ist der <b>eine DD-Regler</b> — disqualifiziert Trials im Lauf <b>und</b> begrenzt die Best-Trial-Auswahl. Das Optimizer-Profil steuert nur noch die <b>Composite-Gewichtung</b> (wie stark PnL / DD / Sharpe die Auswahl gewichten).
        </div>

        {/* UI-Simplify Schritt 2: Risk-Profile-Selector hier ENTFERNT (war eine Dublette des
            Backtest-Tab-Selectors und im Optimizer wirkungslos — Risk wirkt nur aufs Sizing,
            nicht auf die Suche). Das wirksame Control steht im Backtest-Tab; riskProfile-State
            unverändert, fließt weiter in runBt. */}

        {/* Mode selector */}
        <div style={{fontSize:12, fontWeight:700, color:theme.text2, marginBottom:8}}>Optimierungs-Modus</div>
        <div style={{display:"flex", gap:8, marginBottom:16}}>
          {[
            {key:"standard",     label:"Standard",        sub:"IS-Score · ~43ms/Trial",    color:theme.text2},
            {key:"wf_objective", label:"WF-Objective",    sub:"OOS-Score · ~300ms/Trial",  color:theme.blueText, badge:"Empfohlen"},
            {key:"pareto",       label:"Pareto",          sub:"Multi-Obj · NSGA-II",        color:theme.amberText||"#d97706"},
          ].map(({key,label,sub,color,badge}) => {
            const active = runOptMode === key;
            return <button key={key} onClick={()=>setRunOptMode(key)} style={{
              flex:1, padding:"8px 12px", borderRadius:8, cursor:"pointer", textAlign:"left",
              border:`2px solid ${active ? color : theme.border}`,
              background: active ? (key==="wf_objective" ? theme.blueLight : theme.tint) : theme.white,
            }}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <span style={{fontSize:12, fontWeight:700, color: active ? color : theme.text}}>{label}</span>
                {badge && <span style={{fontSize:9, fontWeight:700, padding:"1px 5px", borderRadius:3, background: active ? color : theme.border, color: active ? "#fff" : theme.text3}}>{badge}</span>}
              </div>
              <div style={{fontSize:10, color: active ? color : theme.text3, marginTop:2}}>{sub}</div>
            </button>;
          })}
        </div>

        {/* WF-Objective config */}
        {runOptMode === "wf_objective" && <div style={{padding:"10px 14px", background:theme.blueLight, borderRadius:8, marginBottom:12}}>
          <div style={{fontSize:12, color:theme.blueText, marginBottom:10, lineHeight:1.5}}>
            Bayesian lernt direkt welche Parameter OOS funktionieren. Kein IS-Overfit-Problem.
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 16px"}}>
            {[
              ["IS Split %", wfObjSplit*100, v=>setWfObjSplit(+(v/100).toFixed(2)), 5, 50, 90, "%"],
              ["OOS Windows", wfObjWindows, v=>setWfObjWindows(Math.round(v)), 1, 1, 8, ""],
              ["Min OOS Trades", wfObjMinTrades, v=>setWfObjMinTrades(Math.round(v)), 2, 4, 50, ""],
            ].map(([lbl, val, setter, step, min, max, unit]) => (
              <div key={lbl} style={{padding:"6px 0", borderBottom:`1px solid rgba(37,99,235,0.12)`}}>
                <div style={{fontSize:11, color:theme.blueText, marginBottom:4}}>{lbl}</div>
                <div style={{display:"flex", alignItems:"center", gap:6}}>
                  <input type="number" value={val} step={step} min={min} max={max}
                    onChange={e=>setter(Number(e.target.value))}
                    style={{width:70, padding:"4px 8px", borderRadius:5, border:`1px solid rgba(37,99,235,0.3)`, fontSize:13, fontFamily:fonts.mono, fontWeight:700, background:"rgba(255,255,255,0.7)", color:theme.blueText, textAlign:"right"}}/>
                  {unit && <span style={{fontSize:11, color:theme.blueText}}>{unit}</span>}
                </div>
              </div>
            ))}
          </div>
          <div style={{fontSize:10, color:theme.text3, fontFamily:fonts.mono, marginTop:8}}>
            Geschätzte Laufzeit: ~{Math.round(optTrials * wfObjWindows * 0.3 / 60)} min · Standard: ~{Math.round(optTrials * 0.043 / 60)} min
          </div>
        </div>}

        {/* Pareto config */}
        {runOptMode === "pareto" && <div style={{padding:"10px 14px", background:`rgba(217,119,6,0.06)`, borderRadius:8, marginBottom:12}}>
          <div style={{fontSize:12, color:theme.amberText||"#d97706", marginBottom:10}}>
            NSGA-II optimiert zwei Objectives gleichzeitig → Pareto-Front (Effizienzgrenze). Du wählst deinen Trade-off nach dem Lauf.
          </div>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 16px"}}>
            {[["Objective A (Return, X-Achse)", paretoAx1, setParetoAx1], ["Objective B (Risk, Y-Achse)", paretoAx2, setParetoAx2]].map(([lbl,val,setter]) => (
              <div key={lbl}>
                <div style={{fontSize:11, fontWeight:600, color:theme.text2, marginBottom:4}}>{lbl}</div>
                <select value={val} onChange={e=>setter(e.target.value)} style={{width:"100%", padding:"6px 8px", borderRadius:6, border:`1px solid ${theme.border}`, fontSize:12, background:theme.white, color:theme.text}}>
                  {[["net_pnl","Net P&L ($)"],["max_drawdown_pct","Max DD (%)"],["max_drawdown_usd","Max DD ($)"],["win_rate","Win Rate (%)"],["sharpe","Sharpe Ratio"],["profit_factor","Profit Factor"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>}

        {/* Stability Check toggle */}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", paddingTop:8, borderTop:`1px solid ${theme.divider}`}}>
          <div style={{flex:1}}>
            <div style={{fontSize:12, fontWeight:700, color:theme.text}}>
              Stabilitäts-Check
              <span style={{fontSize:9, padding:"1px 6px", borderRadius:3, background:theme.border, color:theme.text3, marginLeft:8}}>Post-Processing</span>
            </div>
            <div style={{fontSize:11, color:theme.text3, marginTop:2, lineHeight:1.5}}>
              Prüft ob Top-{stabTopN} Trials mit ±{(stabPerturb*100).toFixed(0)}%-Parametervariation noch bestehen. Fragile Strategien bekommen niedrigen Stabilitäts-Score.
            </div>
            {stabEnabled && <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"0 12px", marginTop:8}}>
              {[["Top N", stabTopN, setStabTopN, 5, 5, 50],["Neighbours", stabNeighbours, setStabNeighbours, 2, 2, 20],["Störung %", stabPerturb*100, v=>setStabPerturb(+(v/100).toFixed(3)), 1, 2, 30]].map(([lbl,val,setter,step,min,max]) => (
                <div key={lbl}>
                  <div style={{fontSize:10, color:theme.text3, marginBottom:2}}>{lbl}</div>
                  <input type="number" value={val} step={step} min={min} max={max}
                    onChange={e=>setter(Number(e.target.value))}
                    style={{width:"100%", padding:"4px 8px", borderRadius:5, border:`1px solid ${theme.border}`, fontSize:12, fontFamily:fonts.mono, fontWeight:700, background:theme.white, color:theme.text, textAlign:"right"}}/>
                </div>
              ))}
              <div style={{gridColumn:"span 3", fontSize:10, color:theme.text3, fontFamily:fonts.mono, marginTop:4}}>
                ~{Math.round(stabTopN * stabNeighbours * 0.043 / 60 * 2)} min Zusatz-Zeit nach dem Optimizer-Lauf
              </div>
            </div>}
          </div>
          <div style={{display:"flex", alignItems:"center", gap:8, marginLeft:16, flexShrink:0, marginTop:2}}>
            <span style={{fontSize:11, color:theme.text3}}>{stabEnabled?"An":"Aus"}</span>
            <div onClick={()=>setStabEnabled(s=>!s)} style={{width:40, height:22, borderRadius:11, background:stabEnabled?theme.green:theme.border, cursor:"pointer", position:"relative", transition:"background 0.2s"}}>
              <div style={{width:18, height:18, borderRadius:9, background:"#fff", position:"absolute", top:2, left:stabEnabled?20:2, transition:"left 0.2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
            </div>
          </div>
        </div>
      </div>}

      {/* Date Range */}
      {!opt.running&&<div style={{display:"flex",gap:8,alignItems:"flex-end",marginBottom:16,flexWrap:"wrap"}}>
        <F label="Start"><input type="date" value={optStart} onChange={e=>setOptStart(e.target.value)} style={inp}/></F>
        <F label="End"><input type="date" value={optEnd} onChange={e=>setOptEnd(e.target.value)} style={inp}/></F>
        <F label="Min Trades"><input type="number" value={optMinTrades} onChange={e=>setOptMinTrades(Number(e.target.value))} min={0} max={500} style={{...inp,width:60}}/></F>
        <F label="Max Drawdown %"><input type="number" value={optMaxDD} onChange={e=>setOptMaxDD(Number(e.target.value))} min={0} max={50} style={{...inp,width:60}}/></F>
      </div>}

      {/* Live Run Status */}
      {(opt.running||opt.trials_completed>0)&&<>
        {/* Context Info Bar */}
        <div style={{display:"flex",flexWrap:"wrap",gap:10,marginBottom:14,padding:"8px 14px",borderRadius:8,background:theme.tint,fontSize:11,fontFamily:fonts.mono,color:theme.text3}}>
          <span><strong style={{color:theme.text}}>Instrument:</strong> {opt.instrument||inst}</span>
          <span><strong style={{color:theme.text}}>File:</strong> {opt.strategy_file||stratFile||"Default"}</span>
          <span><strong style={{color:theme.text}}>Mode:</strong> {opt.mode||optMode}</span>
          <span><strong style={{color:theme.text}}>Objective:</strong> {opt.objective||optObj}</span>
          <span><strong style={{color:theme.text}}>Period:</strong> {opt.start_date||optStart} → {opt.end_date||optEnd}</span>
          {selFile?.timeframes&&<span><strong style={{color:theme.text}}>TFs:</strong> {selFile.timeframes.entry||"?"}/{selFile.timeframes.context||"?"}/{selFile.timeframes.regime||"?"}</span>}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:18}}>
          {/* Current Run */}
          <Card className="sl-fadein"><CardHeader title="Aktueller Run" sub={opt.running?`Trial ${opt.trials_completed||0}/${opt.total_trials||optTrials}`:"Abgeschlossen"} right={<div style={{display:"flex",alignItems:"center",gap:8}}>
            {/* Sprint Profile-System: Profile-Badge */}
            {optProfile && <Pill bg={
              optProfile==="conservative" ? theme.blueLight :
              optProfile==="aggressive"   ? "rgba(217,119,6,0.10)" : theme.greenLight
            } color={
              optProfile==="conservative" ? theme.blueText :
              optProfile==="aggressive"   ? (theme.amberText||"#d97706") : theme.greenText
            }>
              {optProfile==="conservative"?"🛡️ Konservativ":optProfile==="aggressive"?"🚀 Aggressiv":"⚖️ Moderat"}
            </Pill>}
            <span style={{fontSize:11,fontFamily:fonts.mono,color:theme.text3}}>{opt.elapsed_seconds?`${Math.floor(opt.elapsed_seconds/3600)}h ${Math.floor((opt.elapsed_seconds%3600)/60)}m ${Math.floor(opt.elapsed_seconds%60)}s${!opt.running?" ✓":""}`:""}</span><Pill bg={opt.running?theme.blueLight:theme.greenLight} color={opt.running?theme.blueText:theme.greenText}>{opt.running?"Running":"Done"}</Pill></div>}/>
            <div style={{padding:"16px 20px"}}>
              {opt.running&&<div style={{marginBottom:16}}><div style={{height:8,borderRadius:4,background:theme.tint}}><div style={{height:"100%",borderRadius:4,background:`linear-gradient(90deg,${theme.blue},${theme.purple})`,width:`${Math.min(opt.progress_pct||0,100)}%`,transition:"width 0.5s"}}/></div><div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:theme.text3,marginTop:5}}><span>{opt.status_message||""}</span><span style={{fontFamily:fonts.mono}}>{opt.trials_completed>5&&opt.elapsed_seconds>0?`~${Math.round(((opt.elapsed_seconds/opt.trials_completed)*(opt.total_trials-opt.trials_completed))/60)} min · ${(opt.elapsed_seconds/opt.trials_completed).toFixed(0)}s/trial`:""}</span></div></div>}
              {!opt.running&&opt.trial_history&&opt.trial_history.length>0&&<div style={{marginBottom:10,display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:11,color:theme.text3,fontWeight:600}}>Auswahl-Kriterium:</span>
                <select value={bestSelection} onChange={e=>setBestSelection(e.target.value)} style={{...sel,fontSize:11,padding:"3px 8px",width:"auto"}}>
                  <option value="best_score">Best Score (Balance)</option>
                  <option value="best_pnl">Best P&L (reiner Profit)</option>
                  <option value="best_pnl_grade_a">Best P&L (nur Grade A)</option>
                  <option value="best_sharpe">Best Sharpe Ratio</option>
                  <option value="best_pf">Best Profit Factor</option>
                  <option value="lowest_dd">Niedrigster Drawdown (Grade A)</option>
                </select>
              </div>}
              {displayBestPnl!=null&&<div style={{padding:16,borderRadius:12,background:theme.greenLight,textAlign:"center"}}>
                <div style={{fontSize:9.5,fontWeight:600,color:theme.text3,textTransform:"uppercase",letterSpacing:"1px",marginBottom:4}}>{computedBest?`Trial #${computedBest.trial} · `:""}Best Result</div>
                <div style={{fontSize:28,fontWeight:900,fontFamily:fonts.mono,color:theme.greenText}}>{formatPnl(displayBestPnl)}</div>
                {displayBestGrade&&<div style={{display:"flex",justifyContent:"center",gap:10,marginTop:6}}><span style={{fontSize:11,color:theme.text3}}>Grade: <strong style={{color:displayBestGrade==="A"?theme.greenText:theme.amberText}}>{displayBestGrade}</strong></span><span style={{fontSize:11,color:theme.text3}}>Kombi: <strong>{displayBestScore!=null?`${Math.round(displayBestScore)}%`:"—"}</strong></span>{computedBest?.total_trades!=null&&<span style={{fontSize:11,color:theme.text3}}>Trades: <strong>{computedBest.total_trades}</strong></span>}{computedBest?.win_rate!=null&&<span style={{fontSize:11,color:theme.text3}}>WR: <strong>{computedBest.win_rate.toFixed(1)}%</strong></span>}</div>}
              </div>}
              {opt.error&&<div style={{padding:10,borderRadius:8,background:theme.redLight,color:theme.redText,fontSize:12,marginTop:10}}>{String(opt.error)}</div>}
            </div>
          </Card>
          {/* Best Parameters */}
          <Card className="sl-fadein"><CardHeader title="Beste Parameter" sub={(() => {
            // Compute "actively optimized" params: those that vary across trials.
            // Constants (be_enabled=true everywhere, tp_mode=partial_trail everywhere) are hardcoded
            // in the optimizer and visual clutter — they don't get optimized.
            const allTrials = opt.trial_history || [];
            const lockedKeys = new Set();
            if (allTrials.length >= 2 && displayBestParams && Object.keys(displayBestParams).length > 0) {
              for (const k of Object.keys(displayBestParams)) {
                const firstVal = allTrials[0]?.params?.[k];
                const isConstant = allTrials.every(t => {
                  const v = t.params?.[k];
                  return v === firstVal || (v == null && firstVal == null);
                });
                if (isConstant) lockedKeys.add(k);
              }
            }
            const optimizedCount = Object.keys(displayBestParams).length - lockedKeys.size;
            return Object.keys(displayBestParams).length > 0
              ? `${optimizedCount} optimiert${lockedKeys.size > 0 ? ` · ${lockedKeys.size} gelockt` : ""}${computedBest ? ` · aus Trial #${computedBest.trial}` : ""}`
              : "Warte…";
          })()}/>
            <div style={{padding:"12px 20px",maxHeight:350,overflow:"auto"}}>
              {Object.keys(displayBestParams).length>0?(()=>{
                const p = displayBestParams;
                const inactive = new Set();
                if (p.be_enabled === false) { inactive.add("be_trigger_rr"); inactive.add("be_offset_ticks"); }
                if (p.trailing_enabled === false) { inactive.add("trailing_atr_mult"); inactive.add("trailing_activation_rr"); }
                if (p.regime_enabled === false) { inactive.add("regime_tf"); inactive.add("regime_mode"); inactive.add("adx_threshold"); inactive.add("atr_sma_length"); }
                if (p.ema_enabled === false) { inactive.add("ema_length"); }
                if (p.fvg_enabled === false) { inactive.add("fvg_max_age"); }
                // Locked keys: konstant über alle Trials → werden nicht optimiert, ausblenden.
                const allTrials = opt.trial_history || [];
                const lockedKeys = new Set();
                if (allTrials.length >= 2) {
                  for (const k of Object.keys(p)) {
                    const firstVal = allTrials[0]?.params?.[k];
                    const isConstant = allTrials.every(t => {
                      const v = t.params?.[k];
                      return v === firstVal || (v == null && firstVal == null);
                    });
                    if (isConstant) lockedKeys.add(k);
                  }
                }
                const visibleEntries = Object.entries(p).filter(([k]) => !lockedKeys.has(k));
                return visibleEntries.map(([k,v],i,a)=>{
                  const deact = inactive.has(k);
                  return <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:i<a.length-1?`1px solid ${theme.divider}`:"none",opacity:deact?0.4:1}} title={deact?"Inaktiv — zugehöriger Toggle ist false":""}>
                    <span style={{fontSize:11.5,color:theme.text2,textDecoration:deact?"line-through":"none"}}>{k}{deact&&<span style={{marginLeft:4,fontSize:9,color:theme.text4}}>(inaktiv)</span>}</span>
                    <span style={{fontSize:12,fontWeight:700,fontFamily:fonts.mono,color:deact?theme.text4:theme.greenText}}>{typeof v==="number"?v.toFixed(4):String(v)}</span>
                  </div>;
                });
              })():<div style={{padding:20,textAlign:"center",color:theme.text3,fontSize:13}}>{opt.running?"Optimiere…":"Optimizer starten."}</div>}
            </div>
          </Card>
        </div>

        {/* v4: Phase-Specific Output removed — struct is YAML-locked, no multi-phase flow */}

        {/* ── result-reorg Eingriff #1: Deploy-Ready + Monte-Carlo als Einheit nach oben
            (direkt unter „Beste Parameter"). Die MC-DD-Quantile sind die Begründung für
            Deploy-Ready → Deploy zuerst, MC direkt darunter. Block-Inhalte unverändert,
            nur Position. ── */}

        {/* v4.4: Deploy-Ready tri-state (YES / NO / INCONCLUSIVE) */}
        {!opt.running && opt.deploy_ready && (() => {
          const dr = opt.deploy_ready.deploy_ready;
          const isYes = dr === true;
          const isNo = dr === false;
          const isInconclusive = dr === null || dr === undefined || dr === "conditional";
          const border = isYes ? theme.green : isNo ? theme.red : theme.amber;
          const icon = isYes ? "✅" : isNo ? "❌" : "⚠️";
          const label = isYes ? "YES" : isNo ? "NO" : (dr === "conditional" ? "CONDITIONAL" : "INCONCLUSIVE");
          const textColor = isYes ? theme.greenText : isNo ? theme.redText : theme.amberText;
          const subText = isInconclusive
            ? "Keine Monte-Carlo-Constraints im YAML definiert. Ergänze goals.monte_carlo.deploy_constraints für Auto-Check."
            : "Strategie geprüft gegen goals.monte_carlo.deploy_constraints";
          return <Card style={{padding:"14px 18px",marginBottom:16,borderLeft:`4px solid ${border}`}} className="sl-fadein">
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <span style={{fontSize:22}}>{icon}</span>
              <div>
                <div style={{fontSize:14,fontWeight:800,color:textColor}}>
                  Deploy-Ready: {label}
                </div>
                <div style={{fontSize:11,color:theme.text3}}>{subText}</div>
              </div>
            </div>
            {opt.deploy_ready.checks && Object.keys(opt.deploy_ready.checks).filter(k => k !== "_no_constraints").length > 0 && <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:8,fontSize:12,fontFamily:fonts.mono}}>
              {Object.entries(opt.deploy_ready.checks).filter(([k]) => k !== "_no_constraints").map(([k,v]) => <div key={k} style={{
                padding:"8px 12px",borderRadius:6,
                background:v.status==="pass"?"rgba(16,185,129,0.08)":"rgba(239,68,68,0.08)",
                border:`1px solid ${v.status==="pass"?"rgba(16,185,129,0.3)":"rgba(239,68,68,0.3)"}`,
              }}>
                <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>{k}</div>
                <div style={{color:v.status==="pass"?theme.greenText:theme.redText,fontWeight:800}}>
                  {v.status==="pass"?"✓":"✗"} actual: {v.actual} / limit: {v.limit}
                </div>
              </div>)}
            </div>}
          </Card>;
        })()}

        {/* v3.7.5: Monte-Carlo Results Card */}
        {!opt.running && opt.monte_carlo && <Card style={{padding:"14px 18px",marginBottom:16}} className="sl-fadein">
          <CardHeader title="Monte-Carlo (Trade Order Permutation)" sub={`${opt.monte_carlo.n_permutations} Permutationen über ${opt.monte_carlo.n_trades} Trades`}/>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,padding:"8px 0"}}>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Max DD Median</div>
              <div style={{fontSize:18,fontWeight:900,fontFamily:fonts.mono}}>${Math.round(opt.monte_carlo.median_max_dd_usd)}</div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Max DD 95%-Quantil</div>
              <div style={{fontSize:18,fontWeight:900,fontFamily:fonts.mono,color:theme.redText}}>${Math.round(opt.monte_carlo.q95_max_dd_usd)}</div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Max DD 99%-Quantil</div>
              <div style={{fontSize:18,fontWeight:900,fontFamily:fonts.mono,color:theme.redText}}>${Math.round(opt.monte_carlo.q99_max_dd_usd)}</div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Final Equity Median</div>
              <div style={{fontSize:16,fontWeight:800,fontFamily:fonts.mono}}>${Math.round(opt.monte_carlo.median_final_equity).toLocaleString()}</div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Final Equity 5%-Quantil</div>
              <div style={{fontSize:16,fontWeight:800,fontFamily:fonts.mono,color:theme.redText}}>${Math.round(opt.monte_carlo.q05_final_equity).toLocaleString()}</div>
            </div>
            <div>
              <div style={{fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>Losing Streak 95%-Quantil</div>
              <div style={{fontSize:16,fontWeight:800,fontFamily:fonts.mono}}>{opt.monte_carlo.q95_longest_losing_streak} Trades</div>
            </div>
          </div>
          <div style={{fontSize:11,color:theme.text3,fontStyle:"italic",marginTop:6}}>
            Bei anderer Trade-Reihenfolge: das 95%-Quantil zeigt den realistischen Worst-Case der Strategie.
          </div>
        </Card>}

        {/* Trial Convergence Chart */}
        {opt.trial_history&&opt.trial_history.length>1&&<Card style={{padding:"12px 16px",marginBottom:16}} className="sl-fadein"><TrialChart trials={opt.trial_history}/></Card>}

        {/* ── Optimizer Summary (after completion) ── */}
        {!opt.running && opt.trial_history && opt.trial_history.length > 0 && (() => {
          const trials = opt.trial_history || [];
          const valid  = trials.filter(t => !t.is_disqualified);
          const dq     = trials.length - valid.length;
          const dqPct  = trials.length > 0 ? dq / trials.length : 0;
          // FIX: Verwende das ECHTE Grade-Feld vom Backend (Quality-Scoring der Pipeline),
          // nicht den client-seitig berechneten compositeScore. compositeScore ist nur ein
          // Percentile-Rank über ALLE Trials — auch wenn nur 1 Trial valid ist, hat der >=70.
          // Das echte Grade ist absolute Qualität ('A' = sehr robust, 'F' = ungenügend).
          const gradeA = valid.filter(t => t.grade === "A").length;
          const gradeAPct = valid.length > 0 ? gradeA / valid.length : 0;
          const profitable = valid.filter(t => (t.net_pnl||0) > 0).length;
          const profPct = valid.length > 0 ? profitable / valid.length : 0;
          const avgPnl  = valid.reduce((s,t)=>s+(t.net_pnl||0),0) / Math.max(valid.length,1);
          const bestPnl = Math.max(...valid.map(t=>t.net_pnl||0));

          // Convergence: are later trials better than early ones?
          const sorted = [...valid].sort((a,b)=>(a.trial||0)-(b.trial||0));
          const half = Math.floor(sorted.length / 2);
          const firstHalf = sorted.slice(0, half);
          const lastHalf  = sorted.slice(half);
          const avgFirst = firstHalf.reduce((s,t)=>s+(t.composite_score||0),0)/Math.max(firstHalf.length,1);
          const avgLast  = lastHalf.reduce((s,t)=>s+(t.composite_score||0),0)/Math.max(lastHalf.length,1);
          const convergencePct = avgFirst > 0 ? Math.round((avgLast-avgFirst)/avgFirst*100) : 0;

          const insights = [];

          // 1. DQ rate — only flag if notable
          if (dqPct > 0.5) insights.push({icon:"⚠️", color:theme.redText,
            text:`${Math.round(dqPct*100)}% der Trials disqualifiziert (${dq}/${trials.length}) — IS-Constraints möglicherweise zu eng. Erwäge min_profit_factor oder max_drawdown_pct zu lockern.`});
          else if (dqPct < 0.05) insights.push({icon:"✅", color:theme.greenText,
            text:`Nur ${dq} von ${trials.length} Trials DQ (${Math.round(dqPct*100)}%) — Constraints gut kalibriert, breite Parametersuche möglich.`});

          // 2. Grade-A concentration — the most important quality signal
          if (gradeAPct >= 0.5) insights.push({icon:"🏆", color:theme.greenText,
            text:`${gradeA} von ${valid.length} validen Trials als Grade A (${Math.round(gradeAPct*100)}%) — sehr robuste Parameter-Landschaft. Hohe Konfidenz in Best-Trial-Auswahl.`});
          else if (gradeAPct < 0.05) insights.push({icon:"❌", color:theme.redText,
            text:`Nur ${gradeA} Grade-A Trial(s) (${Math.round(gradeAPct*100)}% der validen) — fragile Ergebnisse. Erhöhe Trials oder überprüfe Zieldaten-Qualität.`});
          else insights.push({icon:"📊", color:theme.amberText,
            text:`${gradeA} Grade-A Trials (${Math.round(gradeAPct*100)}%) — moderate Robustheit. Ergebnis brauchbar, aber weiteres Tuning könnte helfen.`});

          // 3. P&L landscape — only if clearly positive or negative signal
          if (profPct >= 0.9) insights.push({icon:"💰", color:theme.greenText,
            text:`${Math.round(profPct*100)}% der validen Trials profitabel — breite Gewinnzone. Ø ${formatPnl(avgPnl)}, bestes Trial: ${formatPnl(bestPnl)}.`});
          else if (profPct < 0.4) insights.push({icon:"📉", color:theme.redText,
            text:`Nur ${Math.round(profPct*100)}% der validen Trials profitabel — schmale Gewinnzone. Parameterraum möglicherweise zu breit oder Strategie-Grundprämisse schwach.`});

          // 4. Convergence — did the optimizer actually learn?
          if (convergencePct >= 20) insights.push({icon:"📈", color:theme.blueText,
            text:`Optimizer konvergiert gut — letzte ${half} valide Trials im Schnitt ${convergencePct}% besser als erste ${half}. Bayesian Search hat sinnvolle Bereiche gefunden.`});
          else if (convergencePct < -10) insights.push({icon:"🔀", color:theme.amberText,
            text:`Keine klare Konvergenz erkennbar — spätere Trials nicht besser als frühere. Parameterraum möglicherweise zu groß oder Ziel-Metrik zu flach.`});

          if (!insights.length) return null;

          return <Card style={{marginBottom:18,padding:"16px 20px",background:"rgba(37,99,235,0.02)",border:`1px solid rgba(37,99,235,0.08)`}} className="sl-fadein">
            <div style={{fontSize:14,fontWeight:800,color:theme.text,marginBottom:10}}>📋 Run-Analyse</div>
            {insights.map((ins,i)=><div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<insights.length-1?`1px solid ${theme.divider}`:"none"}}>
              <span style={{fontSize:16,flexShrink:0}}>{ins.icon}</span>
              <span style={{fontSize:13,color:ins.color||theme.text2,lineHeight:1.6}}>{ins.text}</span>
            </div>)}
          </Card>;
        })()}

        {/* ── Suchraum-Diagnose (Backend run_diagnostics) — result-reorg #2: einklappbar, default zu ── */}
        {!opt.running && opt.run_diagnostics && <>
          <button onClick={()=>setShowDiag(p=>!p)} style={{fontSize:13,fontWeight:700,color:theme.blueText,background:"none",border:"none",cursor:"pointer",padding:"8px 0",marginBottom:showDiag?12:0,textDecoration:"underline"}}>
            {showDiag?"▾ Suchraum-Diagnose einklappen":"▸ Suchraum-Diagnose anzeigen"}
          </button>
          {showDiag && (() => {
          const d = opt.run_diagnostics;
          const rows = [];

          // 1. RANGE-RAND — clustern Top-Trials an einer Grenze?
          const flagged = (d.range_edges||[]).filter(e => e.verdict);
          if (flagged.length) {
            flagged.forEach(e => {
              const side = e.verdict === "upper" ? "oberen" : "unteren";
              const wert = e.kind === "numeric" ? (e.verdict==="upper"?e.max:e.min) : e.mode;
              rows.push({icon:"⚠️", color:theme.amberText,
                text:`${e.label} (${e.param}) clustert an der ${side} Grenze (${wert}) — der Optimizer würde gern weiter; erwäge die Range zu weiten.`});
            });
          } else if ((d.range_edges||[]).length) {
            rows.push({icon:"✅", color:theme.greenText,
              text:`Alle ${d.range_edges.length} Suchräume mittig genutzt — keine Range-Grenze schneidet die Top-Trials ab.`});
          }

          // 2. DD-DECKEL — höher-PnL-Trials, die nur am DD scheiterten?
          const dd = d.dd_ceiling;
          if (dd && dd.n > 0) rows.push({icon:"⚠️", color:theme.redText,
            text:`${dd.n} Trial(s) mit bis zu ${formatPnl(dd.max_pnl)} PnL scheiterten NUR am DD-Deckel (${dd.dd_cap}%) — Lockern würde mehr PnL bei mehr Risiko bringen (bester gültiger: ${formatPnl(dd.best_valid_pnl)}).`});
          else if (dd) rows.push({icon:"✅", color:theme.greenText,
            text:`Kein höher-PnL-Trial am DD-Deckel (${dd.dd_cap}%) verloren — der Deckel kostet aktuell keine Rendite.`});

          // 3. KONVERGENZ — wann kam die letzte Best-Verbesserung?
          const c = d.convergence;
          if (c) {
            const pct = Math.round(c.ratio*100);
            if (c.status === "converged") rows.push({icon:"📈", color:theme.blueText,
              text:`Konvergiert — letzte Verbesserung bei Trial ${c.last_improvement_trial}/${c.total_trials} (${pct}%). Mehr Trials bringen wenig.`});
            else if (c.status === "not_exhausted") rows.push({icon:"🔄", color:theme.amberText,
              text:`Noch nicht ausgereizt — letzte Verbesserung erst bei Trial ${c.last_improvement_trial}/${c.total_trials} (${pct}%). Mehr Trials könnten höher kommen.`});
            else rows.push({icon:"📊", color:theme.text2,
              text:`Weitgehend konvergiert — letzte Verbesserung bei Trial ${c.last_improvement_trial}/${c.total_trials} (${pct}%). Mehr Trials evtl. nur marginal.`});
          }

          if (!rows.length) return null;
          return <Card style={{marginBottom:18,padding:"16px 20px",background:"rgba(217,119,6,0.02)",border:`1px solid rgba(217,119,6,0.10)`}} className="sl-fadein">
            <div style={{fontSize:14,fontWeight:800,color:theme.text,marginBottom:4}}>🔬 Suchraum-Diagnose</div>
            <div style={{fontSize:11,color:theme.text3,marginBottom:10}}>Top-{d.params?.top_n} Trials nach net_pnl · {d.params?.n_valid} gültig / {d.params?.n_dq} DQ</div>
            {rows.map((r,i)=><div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<rows.length-1?`1px solid ${theme.divider}`:"none"}}>
              <span style={{fontSize:16,flexShrink:0}}>{r.icon}</span>
              <span style={{fontSize:13,color:r.color||theme.text2,lineHeight:1.6}}>{r.text}</span>
            </div>)}
          </Card>;
        })()}
        </>}

        {/* ── PRIMARY APPLY CTA ── */}
        {!opt.running && opt.trial_history && opt.trial_history.length > 0 && (() => {
          const phaseMode = opt.mode||optMode;
          const phaseLabel = "Tuning";   // v4: single mode
          const nextPhase = "Walk-Forward";
          // R7n-2: hasConsensus also true if a recent saved optimization exists (fallback via history)
          const hasConsensus = (opt.analysis && opt.analysis.insights && opt.analysis.insights.length > 0) || (optRuns && optRuns.length > 0);
          return <Card style={{marginBottom:18,padding:"20px 24px",background:"linear-gradient(135deg,rgba(22,163,74,0.04),rgba(37,99,235,0.04))",border:`2px solid rgba(22,163,74,0.15)`}} className="sl-fadein">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:18,flexWrap:"wrap"}}>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:theme.text,marginBottom:4}}>✅ {phaseLabel} abgeschlossen — Ergebnisse übernehmen</div>
                <div style={{fontSize:13,color:theme.text2,lineHeight:1.6}}>
                  <strong>Empfohlen:</strong> Apply Consensus nutzt die statistische Verteilung aller guten Trials. Robuster als einen einzelnen Trial zu wählen.
                  Danach: <strong>{nextPhase}</strong> starten.
                </div>
              </div>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                {hasConsensus && stratFile && <Button primary style={{background:theme.green,padding:"12px 28px",fontSize:14,fontWeight:800}} onClick={async () => {
                  // R7n-1: If live opt.analysis is missing (e.g. backend restarted after run),
                  // load the most recent saved optimization from history instead.
                  if (opt.analysis) { openConsensus(); return; }
                  const latest = optRuns?.[0];
                  if (!latest?.id) { toast("Kein Optimization-Run in History gefunden", "warn"); return; }
                  try {
                    const full = await getApi(`/results/optimizations/${latest.id}`);
                    if (!full?.analysis) { toast("Optimization hat keine Analyse-Daten", "warn"); return; }
                    openConsensus(full.analysis, full.mode, full.best_params, full.best_net_pnl, full.trial_history);
                  } catch (e) {
                    toast(`Fehler beim Laden: ${e.message}`, "warn");
                  }
                }}>✨ Apply Consensus</Button>}
                {!hasConsensus && <span style={{fontSize:11,color:theme.text3,fontStyle:"italic"}}>Consensus-Analyse nicht verfügbar</span>}
              </div>
            </div>
            <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${theme.divider}`,fontSize:11,color:theme.text3,lineHeight:1.6}}>
              <strong>Alternative:</strong> Einzelnen Trial übernehmen (z.B. wenn du gezielt Trial #X mit bestem Risk-Profil willst) — dafür den <strong>Apply-Button</strong> in der Trial-Tabelle unten nutzen.
            </div>
          </Card>;
        })()}
        {/* ── Top-Kandidaten — result-reorg #3: einklappbar, default zu ── */}
        {!opt.running && opt.trial_history && opt.trial_history.length > 5 && <>
          <button onClick={()=>setShowCand(p=>!p)} style={{fontSize:13,fontWeight:700,color:theme.blueText,background:"none",border:"none",cursor:"pointer",padding:"8px 0",marginBottom:showCand?12:0,textDecoration:"underline"}}>
            {showCand?"▾ Top-Kandidaten einklappen":"▸ Top-Kandidaten anzeigen"}
          </button>
          {showCand && (() => {
          // Calculate ranking by different criteria
          const trials = opt.trial_history;
          const gradeA = trials.filter(t => t.grade === "A");

          // v4-FIX: Exclude disqualified trials from rankings.
          const validTrials = trials.filter(t => !t.is_disqualified);

          // Phase F3 Layer 4 (Bug #74 Fix):
          // Backend (bot/api/scoring.py) ist Single-Source-of-Truth für composite_score.
          // Trials kommen mit composite_score-Feld via enrich_trials_with_composite().
          // -1.0 = Hard-Floor-Fail (excluded). >=0 = valid composite.
          // Frontend rankt NUR noch nach diesem Backend-Feld, keine eigene Berechnung.

          // Rankings per single criterion (für "Sieger pro Kategorie")
          const pnlRank    = new Map([...validTrials].sort((a,b)=>(b.net_pnl||0)-(a.net_pnl||0)).map((t,i)=>[t.trial,i]));
          const sharpeRank = new Map([...validTrials].sort((a,b)=>(b.sharpe||0)-(a.sharpe||0)).map((t,i)=>[t.trial,i]));
          const ddRank = new Map([...validTrials].sort((a,b)=>(a.max_drawdown||999)-(b.max_drawdown||999)).map((t,i)=>[t.trial,i]));
          const pfRank = new Map([...validTrials].sort((a,b)=>(b.profit_factor||0)-(a.profit_factor||0)).map((t,i)=>[t.trial,i]));
          const wrRank = new Map([...validTrials].sort((a,b)=>(b.win_rate||0)-(a.win_rate||0)).map((t,i)=>[t.trial,i]));

          // Multi-criteria Top-10: nach Backend composite_score sortiert (0-100, descending).
          // Trials mit composite_score=-1 (Hard-Floor-Fail) werden ausgeschlossen.
          const multiCriteria = [...validTrials]
            .filter(t => (t.composite_score ?? -1) >= 0)
            .sort((a,b) => (b.composite_score||0) - (a.composite_score||0))
            .slice(0, 10);

          // Also compute: top by each single criterion — all from validTrials only
          const topByPnl = [...validTrials].sort((a,b)=>(b.net_pnl||0)-(a.net_pnl||0))[0];
          const topBySharpe = [...validTrials].sort((a,b)=>(b.sharpe||0)-(a.sharpe||0))[0];
          const topByDd = [...(gradeA.length>0?gradeA:validTrials)].sort((a,b)=>(a.max_drawdown||999)-(b.max_drawdown||999))[0];
          const topByPf = [...validTrials].sort((a,b)=>(b.profit_factor||0)-(a.profit_factor||0))[0];
          // "Best Balance" jetzt = Backend canonical Best (Hybrid Eligibility-Layer).
          // Vorher Bug #76: war topByScore (Bayesian), nicht aligned mit "Best Trial" UI.
          // Now: nutze Backend best_trial_num oder fallback auf composite_score-Top
          const topByComposite = (() => {
            if (opt.best_trial_num != null) {
              const canonical = validTrials.find(t => t.trial === opt.best_trial_num);
              if (canonical) return canonical;
            }
            return multiCriteria[0] || null;
          })();

          return <Card style={{marginBottom:18}} className="sl-fadein">
            <CardHeader
              title="🏆 Top Kandidaten — Multi-Kriterien Analyse"
              sub="Die 10 Trials mit der besten Kombination aus P&L, Sharpe, DD, PF und Win Rate"
            />
            {/* Category winners */}
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${theme.divider}`,background:theme.tint}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.text2,marginBottom:10}}>Sieger pro Kategorie</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:10}}>
                {[
                  {label:"💰 Höchster P&L",trial:topByPnl,metric:formatPnl(topByPnl?.net_pnl),color:theme.greenText},
                  {label:"⚖️ Best Balance",trial:topByComposite,metric:topByComposite?.composite_score!=null?`${Math.round(topByComposite.composite_score)}%`:`Score ${Number(topByComposite?.score||0).toFixed(2)}`,color:theme.blueText},
                  {label:"📈 Best Sharpe",trial:topBySharpe,metric:Number(topBySharpe?.sharpe||0).toFixed(2),color:theme.purpleText||"#7C3AED"},
                  {label:"🛡️ Niedrigster DD",trial:topByDd,metric:`${Number(topByDd?.max_drawdown||0).toFixed(1)}%`,color:theme.redText},
                  {label:"🎯 Best PF",trial:topByPf,metric:Number(topByPf?.profit_factor||0).toFixed(2),color:theme.amberText},
                ].map((cat,i) => cat.trial && <div key={i} style={{padding:"10px 12px",borderRadius:8,background:theme.white,border:`1px solid ${theme.divider}`,cursor:"pointer"}} onClick={()=>setSelTrial(cat.trial)}>
                  <div style={{fontSize:11,fontWeight:600,color:theme.text3,marginBottom:4}}>{cat.label}</div>
                  <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
                    <span style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:cat.color}}>{cat.metric}</span>
                    <span style={{fontSize:10,color:theme.text4,fontFamily:fonts.mono}}>#{cat.trial.trial}</span>
                  </div>
                  <div style={{fontSize:10,color:theme.text3,marginTop:3,fontFamily:fonts.mono}}>
                    P&L {formatPnl(cat.trial.net_pnl)} · WR {cat.trial.win_rate?.toFixed(1)}% · DD {cat.trial.max_drawdown?.toFixed(1)}%
                  </div>
                </div>)}
              </div>
            </div>

            {/* Top 10 Multi-Criteria Table */}
            <div style={{padding:"14px 20px"}}>
              <div style={{fontSize:12,fontWeight:700,color:theme.text2,marginBottom:10}}>Top 10 — beste Kombination aller Metriken</div>
              <div style={{overflow:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse"}}>
                  <thead><tr style={{borderBottom:`2px solid ${theme.divider}`}}>
                    {["Rang","Trial","P&L","Kombi","PF","Sharpe","WR","DD","Trades","Stabil.","Action"].map(h=>
                      <th key={h} style={{padding:"6px 8px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:h==="Rang"?"center":"left",whiteSpace:"nowrap"}}>{h}</th>
                    )}
                  </tr></thead>
                  <tbody>{multiCriteria.map((t,i) => <tr key={t.trial} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`,cursor:"pointer",background:i===0?"rgba(22,163,74,0.04)":"transparent"}} onClick={()=>setSelTrial(t)}>
                    <td style={{padding:"5px 8px",textAlign:"center"}}>{i<3?<span style={{fontSize:14}}>{["🥇","🥈","🥉"][i]}</span>:<span style={{fontSize:11,fontFamily:fonts.mono,color:theme.text4}}>{i+1}</span>}</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono,fontWeight:700}}>#{t.trial}</td>
                    <td style={{padding:"5px 8px",fontSize:11.5,fontWeight:700,fontFamily:fonts.mono,color:t.net_pnl>0?theme.greenText:theme.redText}}>{formatPnl(t.net_pnl)}</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontWeight:700,fontFamily:fonts.mono,color:(t.composite_score||0)>=70?theme.greenText:(t.composite_score||0)>=40?theme.amberText:theme.text3}}>{t.composite_score||0}%</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{Number(t.profit_factor||0).toFixed(2)}</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{Number(t.sharpe||0).toFixed(2)}</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{t.win_rate?.toFixed(1)}%</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono,color:theme.redText}}>{t.max_drawdown?.toFixed(1)}%</td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{t.total_trades}</td>
                    <td style={{padding:"5px 8px",fontSize:10,fontFamily:fonts.mono,color:theme.blueText}}>
                      {(t.params?.entry_tf || t.params?.context_tf || t.params?.regime_tf) ? <>
                        {t.params?.entry_tf||"—"}{t.params?.context_tf && <span style={{opacity:0.4}}>/{t.params.context_tf}</span>}{t.params?.regime_tf && <span style={{opacity:0.4}}>/{t.params.regime_tf}</span>}
                      </> : <span style={{color:theme.text4}}>—</span>}
                    </td>
                    <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono,
                      color:t.stability_score!=null?(t.stability_score>=0.6?theme.greenText:t.stability_score>=0.3?theme.amberText:theme.redText):theme.text4}}
                      title={t.stability_score!=null?`${t.stability_passed||"?"}/${t.stability_tested||"?"} Nachbarn valid`:"Stability-Check nicht aktiv"}>
                      {t.stability_score!=null?`${(t.stability_score*100).toFixed(0)}%`:"—"}</td>
                    <td style={{padding:"5px 8px"}}>{t.params&&Object.keys(t.params).length>0&&<button onClick={e=>{e.stopPropagation();applyOptToYaml(bestParamsToApplyData(t.params));}} className="sl-btn" style={{fontSize:9,padding:"3px 8px",borderRadius:4,border:`1px solid rgba(22,163,74,0.2)`,background:theme.greenLight,color:theme.greenText,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>Apply</button>}</td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div style={{marginTop:12,padding:"10px 14px",borderRadius:8,background:theme.tint,fontSize:12,color:theme.text2,lineHeight:1.6}}>
                💡 <strong>Kombi-Score</strong>: prozentualer Rang über P&L (30%), Sharpe (25%), DD (20%), PF (15%), WR (10%) — unabhängig vom Bayesian-Score. <strong>Stabil.</strong>: % der Nachbar-Parametrisierungen die ebenfalls bestehen (nur wenn Stabilitäts-Check aktiv war). Ein Top-10 Platz bedeutet: der Trial ist in keiner Metrik schlecht.
              </div>
            </div>
          </Card>;
        })()}
        </>}

        {/* ── All Trials (collapsed by default) ── */}
        {opt.trial_history&&opt.trial_history.length>0&&<>
          <button onClick={()=>setSelTrial(selTrial===false?null:false)} style={{fontSize:13,fontWeight:700,color:theme.blueText,background:"none",border:"none",cursor:"pointer",padding:"8px 0",marginBottom:selTrial!==false?0:12,textDecoration:"underline"}}>
            {selTrial===false?"▾ Trial-Tabelle einklappen":`▸ Trial-Tabelle anzeigen (${opt.trials_completed||opt.trial_history.length} Trials)`}
          </button>

          {selTrial!==false&&selTrial&&<Card style={{marginBottom:14,border:`2px solid ${theme.blue}`}} className="sl-fadein">
            <CardHeader title={`Trial #${selTrial.trial}`} sub={`Kombi: ${selTrial.composite_score!=null?selTrial.composite_score+"%":"—"} · Bayesian-Score: ${Number(selTrial.score||0).toFixed(2)} · Grade: ${selTrial.grade||"?"}`} right={<Button onClick={()=>setSelTrial(false)}>Schließen</Button>}/>
            <div style={{padding:"12px 20px"}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8,marginBottom:14}}>
                {[["P&L",formatPnl(selTrial.net_pnl),selTrial.net_pnl>0?theme.greenText:theme.redText],["PF",Number(selTrial.profit_factor||0).toFixed(2),selTrial.profit_factor>1.3?theme.greenText:theme.text],["Sharpe",Number(selTrial.sharpe||0).toFixed(2),selTrial.sharpe>1.5?theme.greenText:theme.text3],["Win Rate",`${(selTrial.win_rate||0).toFixed(1)}%`,selTrial.win_rate>55?theme.greenText:theme.text3],["Max DD",`${(selTrial.max_drawdown||0).toFixed(1)}%`,theme.redText],["Trades",selTrial.total_trades||0,theme.text]].map(([l,v,c])=><div key={l}><div style={{fontSize:8.5,fontWeight:700,color:theme.text4,textTransform:"uppercase"}}>{l}</div><div style={{fontSize:14,fontWeight:800,fontFamily:fonts.mono,color:c}}>{v}</div></div>)}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:4,maxHeight:250,overflow:"auto"}}>
                {selTrial.params&&(()=>{
                  // Detect deactivated params (parent toggle = false makes them irrelevant)
                  const p = selTrial.params;
                  const inactive = new Set();
                  if (p.be_enabled === false) { inactive.add("be_trigger_rr"); inactive.add("be_offset_ticks"); }
                  if (p.trailing_enabled === false) { inactive.add("trailing_atr_mult"); inactive.add("trailing_activation_rr"); }
                  if (p.regime_enabled === false) { inactive.add("regime_tf"); inactive.add("regime_mode"); inactive.add("adx_threshold"); inactive.add("atr_sma_length"); }
                  if (p.ema_enabled === false) { inactive.add("ema_length"); }
                  if (p.fvg_enabled === false) { inactive.add("fvg_max_age"); }
                  return Object.entries(selTrial.params).map(([k,v])=>{
                    const deact = inactive.has(k);
                    return <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"4px 8px",borderRadius:4,background:deact?"transparent":theme.tint,opacity:deact?0.35:1,textDecoration:deact?"line-through":"none"}} title={deact?"Inaktiv — Parent-Toggle ist false":""}>
                      <span style={{fontSize:10.5,color:theme.text3}}>{k}{deact&&<span style={{marginLeft:4,fontSize:9,color:theme.text4}}>(inaktiv)</span>}</span>
                      <span style={{fontSize:10.5,fontWeight:700,fontFamily:fonts.mono,color:deact?theme.text4:typeof v==="boolean"?(v?theme.greenText:theme.redText):theme.text}}>{typeof v==="number"?v.toFixed(3):String(v)}</span>
                    </div>;
                  });
                })()}
              </div>
            </div>
          </Card>}

          {selTrial===false&&(()=>{
            const cols = [
              {k:"trial",l:"#"},{k:"score",l:"Score"},{k:"net_pnl",l:"P&L"},
              {k:"profit_factor",l:"PF"},{k:"sharpe",l:"Sharpe"},{k:"win_rate",l:"WR"},
              {k:"max_drawdown",l:"DD"},{k:"total_trades",l:"Trades"},{k:"grade",l:"Grade"},
            ];
            const propFirm = selFile?.prop_firm;
            const hasPropFirm = propFirm && propFirm.broker === "topstep";
            const accountSize = propFirm?.account_size || 50000;
            // Check each trial against prop firm limits
            const checkTrialVsPropFirm = (t) => {
              if (!hasPropFirm) return { ok: true };
              const violations = [];
              // Max DD in $ (rough estimate: DD% * account)
              const ddDollars = (t.max_drawdown || 0) / 100 * accountSize;
              if (propFirm.max_loss_limit && ddDollars > propFirm.max_loss_limit) {
                violations.push(`DD $${ddDollars.toFixed(0)} > MLL $${propFirm.max_loss_limit}`);
              }
              // Profit target (for Combine)
              if (propFirm.profit_target && (t.net_pnl || 0) < propFirm.profit_target) {
                violations.push(`P&L $${t.net_pnl?.toFixed(0)} < Target $${propFirm.profit_target}`);
              }
              return { ok: violations.length === 0, violations };
            };
            const toggleSort = k => setTrialSort(s => ({key:k, dir:s.key===k && s.dir==="desc" ? "asc" : "desc"}));
            const sortedTrials = [...opt.trial_history].sort((a,b) => {
              const va = a[trialSort.key]; const vb = b[trialSort.key];
              if (trialSort.key === "grade") {
                const order = {A:0,B:1,C:2,D:3,F:4};
                const oa = order[va]??99; const ob = order[vb]??99;
                return trialSort.dir==="asc" ? oa-ob : ob-oa;
              }
              const na = Number(va)||0; const nb = Number(vb)||0;
              return trialSort.dir==="asc" ? na-nb : nb-na;
            });
            return <Card style={{marginBottom:18}}><div style={{maxHeight:500,overflow:"auto"}}>
              <div style={{padding:"8px 14px",borderBottom:`1px solid ${theme.divider}`,fontSize:11,color:theme.text3,fontWeight:600,background:theme.tint,position:"sticky",top:0,zIndex:2,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{opt.trial_history.length} Trials · Klicke Spalten-Header zum Sortieren · Klicke Zeile für Details</span>
                {hasPropFirm && <span style={{color:(theme.purpleText||"#7C3AED"),fontWeight:700,fontSize:11}}>💎 {propFirm.type?.replace(/_/g," ")} Validation aktiv</span>}
              </div>
              <table style={{width:"100%",borderCollapse:"collapse"}}>
                <thead><tr style={{borderBottom:`2px solid ${theme.divider}`,position:"sticky",top:34,background:theme.white,zIndex:1}}>
                  {cols.map(c => <th key={c.k} onClick={()=>toggleSort(c.k)} style={{padding:"6px 8px",fontSize:9,fontWeight:700,color:trialSort.key===c.k?theme.blueText:theme.text4,textTransform:"uppercase",textAlign:"left",cursor:"pointer",userSelect:"none",whiteSpace:"nowrap"}}>
                    {c.l}{trialSort.key===c.k ? (trialSort.dir==="desc"?" ▼":" ▲") : ""}
                  </th>)}
                  {hasPropFirm && <th style={{padding:"6px 8px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"center",whiteSpace:"nowrap"}}>Prop Firm</th>}
                  <th style={{padding:"6px 8px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"right"}}>Apply</th>
                </tr></thead>
                <tbody>{sortedTrials.map(t => {
                  const propCheck = checkTrialVsPropFirm(t);
                  return <tr key={t.trial} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`,cursor:"pointer",background:selTrial?.trial===t.trial?"rgba(37,99,235,0.05)":!propCheck.ok?"rgba(239,68,68,0.03)":computedBest?.trial===t.trial?"rgba(22,163,74,0.04)":"transparent"}} onClick={()=>setSelTrial(t)}>
                    <td style={{padding:"4px 8px",fontSize:10,fontFamily:fonts.mono,color:theme.text3}}>{t.trial}{computedBest?.trial===t.trial&&<span style={{marginLeft:4,color:theme.greenText}}>★</span>}</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontWeight:700,fontFamily:fonts.mono,color:(t.score||0)>=0.8?theme.greenText:(t.score||0)>=0.4?theme.amberText:theme.redText}}>{Number(t.score||0).toFixed(2)}</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono,color:t.net_pnl>0?theme.greenText:theme.redText}}>{formatPnl(t.net_pnl)}</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono}}>{Number(t.profit_factor||0).toFixed(2)}</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono}}>{Number(t.sharpe||0).toFixed(2)}</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono}}>{t.win_rate?.toFixed(1)}%</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono,color:theme.redText}}>{t.max_drawdown?.toFixed(1)}%</td>
                    <td style={{padding:"4px 8px",fontSize:10.5,fontFamily:fonts.mono}}>{t.total_trades}</td>
                    <td style={{padding:"4px 8px"}}><Pill bg={t.grade==="A"?theme.greenLight:t.grade==="B"?theme.blueLight:theme.tint} color={t.grade==="A"?theme.greenText:t.grade==="B"?theme.blueText:theme.text3}>{t.grade}</Pill></td>
                    {hasPropFirm && <td style={{padding:"4px 8px",textAlign:"center"}} title={propCheck.ok?"Erfüllt alle Prop Firm Limits":`Verletzt: ${(propCheck.violations||[]).join("; ")}`}>
                      {propCheck.ok ? <span style={{fontSize:14,color:theme.greenText}}>✓</span> : <span style={{fontSize:14,color:theme.redText}}>⚠</span>}
                    </td>}
                    <td style={{padding:"4px 8px",textAlign:"right"}}>{t.params&&Object.keys(t.params).length>0&&<button onClick={e=>{e.stopPropagation();applyOptToYaml(bestParamsToApplyData(t.params));}} className="sl-btn" style={{fontSize:9,padding:"3px 8px",borderRadius:4,border:`1px solid rgba(37,99,235,0.2)`,background:theme.blueLight,color:theme.blueText,cursor:"pointer",fontWeight:700,whiteSpace:"nowrap"}}>Apply</button>}</td>
                  </tr>;
                })}</tbody>
              </table></div></Card>;
          })()}
        </>}

        {/* ── Trial Analysis + Consensus (collapsible) ── */}
        {opt.analysis&&opt.analysis.insights&&opt.analysis.insights.length>0&&<>
          <button onClick={()=>setShowRng(p=>!p)} style={{fontSize:13,fontWeight:700,color:theme.blueText,background:"none",border:"none",cursor:"pointer",padding:"8px 0",marginBottom:showRng?12:0,textDecoration:"underline"}}>
            {showRng?"▾ Detaillierte Analyse einklappen":"▸ Detaillierte Trial-Analyse anzeigen"}
          </button>

          {showRng&&<Card style={{marginBottom:16}} className="sl-fadein">
            {/* R7t-1: Determine which features are DISABLED based on categorical consensus */}
            {(() => {
              // This IIFE just precomputes disabled-features for use in panels below
              const cat = opt.analysis.categorical || {};
              const isDisabled = (flagKey) => {
                const f = cat[flagKey];
                return f && f.recommendation === "False" && f.confidence >= 70;
              };
              // Map: parameter-key → parent-flag-key (if the feature is off, param is irrelevant)
              const paramDeps = {
                // BE params depend on be_enabled
                be_mode: "be_enabled", be_trigger_rr: "be_enabled", be_after_rr: "be_enabled",
                // Regime params depend on regime_enabled
                regime_mode: "regime_enabled", regime_tf: "regime_enabled",
                adx_threshold: "regime_enabled", atr_sma_length: "regime_enabled",
                // EMA params depend on ema_enabled
                ema_mode: "ema_enabled", ema_length: "ema_enabled", ctx_ema_length: "ema_enabled",
                // FVG params depend on fvg_enabled
                fvg_proximity_enabled: "fvg_enabled", fvg_min_size: "fvg_enabled",
                // Loss scaling params depend on loss_scaling_enabled
                loss_scale_1: "loss_scaling_enabled", loss_scale_2: "loss_scaling_enabled",
                // HTF confirmation toggles depend on their own enables (different structure - handle separately)
                // Trail params depend on tp_mode
                trail_mode: null, // handled differently
              };
              // Expose globally for JSX below (using var because of hoisting)
              opt._paramDeps = paramDeps;
              opt._isFeatureDisabled = isDisabled;
              opt._disabledFeatures = Object.keys(cat).filter(k => k.endsWith("_enabled") && isDisabled(k));
              return null;
            })()}

            {/* R7t-2: Warning banner about irrelevant params */}
            {opt._disabledFeatures.length > 0 && <div style={{padding:"10px 20px", background:"rgba(245,158,11,0.06)", borderBottom:`1px solid ${theme.divider}`, display:"flex", alignItems:"flex-start", gap:10}}>
              <span style={{fontSize:16, lineHeight:"18px"}}>💡</span>
              <div style={{fontSize:11.5, color:theme.text2, lineHeight:1.55}}>
                <strong>Hinweis zur Interpretation:</strong> {opt._disabledFeatures.length} Feature{opt._disabledFeatures.length>1?"s":""} {opt._disabledFeatures.length>1?"wurden":"wurde"} vom Optimizer ausgeschaltet
                ({opt._disabledFeatures.join(", ")}). Parameter die zu ausgeschalteten Features gehören
                (z.B. <code style={{fontFamily:fonts.mono,fontSize:10,background:theme.tint,padding:"1px 4px",borderRadius:3}}>be_trigger_rr</code> bei <code style={{fontFamily:fonts.mono,fontSize:10,background:theme.tint,padding:"1px 4px",borderRadius:3}}>be_enabled=False</code>)
                sind unten ausgegraut — deren Werte haben <strong>keinen Einfluss</strong> auf die Strategy und sind statistisches Rauschen.
                Apply Consensus ignoriert sie korrekt.
              </div>
            </div>}

            {/* Top Insights (max 6 most important) */}
            <div style={{padding:"14px 20px",borderBottom:`1px solid ${theme.divider}`}}>
              <div style={{fontSize:13,fontWeight:700,color:theme.text,marginBottom:10}}>Erkenntnisse ({opt.analysis.total_analyzed} Trials analysiert)</div>
              {opt.analysis.insights.filter((_,i)=>i<6).map((ins,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:i<Math.min(opt.analysis.insights.length,6)-1?`1px solid ${theme.divider}`:"none"}}><span style={{width:8,height:8,borderRadius:"50%",flexShrink:0,background:ins.type==="strong"?theme.green:ins.type==="moderate"?theme.amber:theme.blue}}/><span style={{fontSize:12,color:theme.text2,fontFamily:ins.type==="numerical"?fonts.mono:fonts.body}}>{ins.message}</span></div>)}
              {opt.analysis.insights.length>6&&<div style={{fontSize:11,color:theme.text4,marginTop:6}}>+{opt.analysis.insights.length-6} weitere Erkenntnisse</div>}
            </div>

            {/* Categorical Decisions - R7t-3: grey out dependent params */}
            {opt.analysis.categorical&&Object.keys(opt.analysis.categorical||{}).length>0&&<div style={{padding:"14px 20px",borderBottom:`1px solid ${theme.divider}`}}>
              <div style={{fontSize:13,fontWeight:700,color:theme.text,marginBottom:10}}>Struktur-Entscheidungen</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8}}>
                {Object.entries(opt.analysis.categorical||{}).map(([k,v])=>{
                  const parentFlag = opt._paramDeps?.[k];
                  const isIrrelevant = parentFlag && opt._isFeatureDisabled?.(parentFlag);
                  // Backend liefert top_pick + top_distribution (frühere Code-Stand: recommendation + top).
                  // Fallback auf beide Namen für Vorwärts/Rückwärts-Kompatibilität.
                  const pick = v.top_pick ?? v.recommendation;
                  const dist = v.top_distribution ?? v.top ?? {};
                  return <div key={k} style={{padding:"10px 12px",borderRadius:8,background:isIrrelevant?theme.tint:(v.confidence>=70?theme.greenLight:v.confidence>=55?theme.amberLight:theme.tint),opacity:isIrrelevant?0.5:1,position:"relative"}} title={isIrrelevant?`${parentFlag}=False → dieser Wert ist nicht relevant`:undefined}>
                    {isIrrelevant && <div style={{position:"absolute",top:4,right:6,fontSize:9,color:theme.text4,fontWeight:700}}>⊘ inaktiv</div>}
                    <div style={{fontSize:10,fontWeight:700,color:theme.text3,marginBottom:3,textDecoration:isIrrelevant?"line-through":"none"}}>{k}</div>
                    <div style={{fontSize:14,fontWeight:800,fontFamily:fonts.mono,color:isIrrelevant?theme.text4:(v.confidence>=70?theme.greenText:theme.text)}}>{pick!=null?String(pick):"—"}</div>
                    <div style={{fontSize:10,color:theme.text3,marginTop:3}}>
                      <span style={{fontWeight:700}}>{v.confidence}%</span> · {Object.entries(dist).map(([val,pct])=>`${val} ${pct}%`).join(" | ")}
                    </div>
                  </div>;
                })}
              </div>
            </div>}

            {/* Numerical Sweet Spots - R7t-4: grey out dependent params */}
            {opt.analysis.numerical&&Object.keys(opt.analysis.numerical||{}).length>0&&<div style={{padding:"14px 20px"}}>
              <div style={{fontSize:13,fontWeight:700,color:theme.text,marginBottom:10}}>Parameter Sweet Spots</div>
              <div style={{overflow:"auto",maxHeight:250}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`1px solid ${theme.divider}`}}>
                {["Parameter","Top Ø","Alle Ø","Bottom Ø","Sweet Spot","Status"].map(h=><th key={h} style={{padding:"6px 8px",fontSize:10,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"left"}}>{h}</th>)}
              </tr></thead><tbody>
                {Object.entries(opt.analysis.numerical||{}).map(([k,v])=>{
                  const parentFlag = opt._paramDeps?.[k];
                  const isIrrelevant = parentFlag && opt._isFeatureDisabled?.(parentFlag);
                  return <tr key={k} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`, opacity:isIrrelevant?0.45:1}} title={isIrrelevant?`${parentFlag}=False → nicht verwendet`:undefined}>
                    <td style={{padding:"5px 8px",fontSize:12,fontWeight:600,textDecoration:isIrrelevant?"line-through":"none",color:isIrrelevant?theme.text4:theme.text}}>{k}</td>
                    <td style={{padding:"5px 8px",fontSize:12,fontFamily:fonts.mono,fontWeight:700,color:isIrrelevant?theme.text4:theme.greenText}}>{v.top_mean}</td>
                    <td style={{padding:"5px 8px",fontSize:12,fontFamily:fonts.mono,color:theme.text3}}>{v.all_mean}</td>
                    <td style={{padding:"5px 8px",fontSize:12,fontFamily:fonts.mono,color:isIrrelevant?theme.text4:theme.redText}}>{v.bottom_mean}</td>
                    <td style={{padding:"5px 8px",fontSize:12,fontFamily:fonts.mono,fontWeight:700,color:isIrrelevant?theme.text4:theme.blueText}}>{(v.top_p10!=null&&v.top_p90!=null)?`[${v.top_p10} – ${v.top_p90}]`:(v.recommended_min!=null?`[${v.recommended_min} – ${v.recommended_max}]`:"—")}</td>
                    <td style={{padding:"5px 8px",fontSize:10}}>{isIrrelevant?<Pill bg={theme.tint} color={theme.text4} style={{fontSize:9}}>⊘ {parentFlag}=off</Pill>:<Pill bg={theme.greenLight} color={theme.greenText} style={{fontSize:9}}>✓ aktiv</Pill>}</td>
                  </tr>;
                })}
              </tbody></table></div>
            </div>}

            {/* Grade Distribution + Consensus Action */}
            {opt.analysis.grade_distribution&&<div style={{padding:"12px 20px",borderTop:`1px solid ${theme.divider}`,display:"flex",gap:12,alignItems:"center"}}>
              <span style={{fontSize:11,fontWeight:600,color:theme.text3}}>Grades:</span>
              {Object.entries(opt.analysis.grade_distribution||{}).map(([g,n])=><Pill key={g} bg={g==="A"?theme.greenLight:g==="B"?theme.blueLight:g==="C"?theme.amberLight:theme.redLight} color={g==="A"?theme.greenText:g==="B"?theme.blueText:g==="C"?theme.amberText:theme.redText}>{g}: {n}</Pill>)}
              <span style={{fontSize:11,color:theme.text3,marginLeft:"auto"}}>Top-25% Ø: <strong style={{color:theme.greenText}}>{formatPnl(opt.analysis.top_avg_pnl)}</strong></span>
              <Button primary className="sl-btn" onClick={openConsensus}>Apply Consensus</Button>
            </div>}
          </Card>}

          {/* Consensus Panel — moved to outer optimizer fragment in R7f-4 so it renders in both list and detail views */}
        </>}
      </>}

      <SectionTitle>Optimization History</SectionTitle>
      {filterRuns(optRuns).length>0?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>{filterRuns(optRuns).map((r,i)=><RunCard key={r.id||i} run={r} onClick={r.id?()=>setOptDet(r.id):undefined} onDelete={id=>deleteRun(id,"optimizations")} onHide={hideRun}/>)}</div>:<Empty icon="bolt" title="Keine Optimierungen" sub="Starte den Optimizer." btn="Start" onClick={runOpt}/>}
      {nextStep&&<div style={{marginTop:20,display:"flex",justifyContent:"flex-end"}}><Button primary className="sl-btn" onClick={()=>handleTabChange(nextStep)}>Weiter: {nextLabel[nextStep]} →</Button></div>}
    </>}
    </>}


    {/* ═══ WALK-FORWARD ═══ */}
    {tab==="walkforward" && (wfDet?<WFDetail runId={wfDet} onClose={()=>setWfDet(null)}/>:<>
      {/* R7p: Info-Panel erklärt Walk-Forward Konzept */}
      <Card style={{marginBottom:16,padding:"14px 18px",background:"rgba(37,99,235,0.03)",border:`1px solid rgba(37,99,235,0.15)`}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
          <span style={{fontSize:16}}>🛡️</span>
          <div style={{fontSize:13,fontWeight:800,color:theme.text}}>Walk-Forward-Validierung</div>
        </div>
        <div style={{fontSize:12,color:theme.text2,lineHeight:1.6,marginBottom:10}}>
          Simuliert wie deine Strategy in der Realität funktioniert: Training auf alten Daten → Test auf neuen, ungesehenen Daten. Das ist der härteste Reality-Check gegen Overfitting.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))",gap:10,fontSize:11,color:theme.text2,lineHeight:1.5}}>
          <div>
            <div style={{fontWeight:700,color:theme.blueText,marginBottom:2}}>📚 IS = In-Sample</div>
            Trainingsperiode pro Fenster. Optimizer tunt hier die Parameter. Länger = stabiler, kürzer = mehr Fenster.
          </div>
          <div>
            <div style={{fontWeight:700,color:theme.greenText,marginBottom:2}}>🎯 OOS = Out-of-Sample</div>
            Testperiode auf <strong>ungesehenen</strong> Daten. Das ist was live zählt. Die aggregierte OOS-Performance ist dein Wahrheits-Check.
          </div>
          <div>
            <div style={{fontWeight:700,color:theme.amberText,marginBottom:2}}>🔁 Trials/Window</div>
            Wie oft der Optimizer pro Fenster sucht. Mehr = bessere IS-Parameter, aber langsamer. 200-300 ist Sweet-Spot.
          </div>
        </div>
      </Card>

      {/* R7p: Mode-Beschreibungen als Info-Box */}
      <Card style={{marginBottom:16,padding:"12px 16px",background:theme.tint}}>
        <div style={{fontSize:12,fontWeight:700,color:theme.text,marginBottom:8}}>📋 v4: Parameter-Tuning pro Fenster</div>
        <div style={{padding:"10px 14px",background:theme.white,borderRadius:6,border:`1px solid ${theme.divider}`,fontSize:11,lineHeight:1.5}}>
          <div style={{fontSize:12,fontWeight:700,color:theme.greenText,marginBottom:3}}>tuning</div>
          <div style={{color:theme.text2}}>Pro IS-Fenster werden die 11 parametrischen Werte + 5 Feature-Toggles mit <strong>bucket_robust_score</strong> getuned. Struktur (TFs, sl_mode, direction_filter, entry_mode, break_confirm) ist YAML-gelockt und ändert sich nicht. Cross-Instrument-Validation (MNQ+FDXS) läuft automatisch nach allen Fenstern wenn Verdict=ROBUST.</div>
        </div>
      </Card>

      {/* R7p: Größere Input-Felder */}
      <Card style={{marginBottom:16,padding:"16px 20px"}}>
        <div style={{fontSize:12,fontWeight:700,color:theme.text,marginBottom:12}}>⚙️ Konfiguration</div>
        <div style={{display:"flex",gap:18,alignItems:"flex-end",flexWrap:"wrap"}}>
          <F label="IS Monate">
            <input type="number" value={wfIsM} onChange={e=>setWfIsM(Number(e.target.value))} min={3} max={24} style={{...inp,width:85,fontSize:15,fontWeight:700,padding:"10px 12px"}}/>
            <div style={{fontSize:10,color:theme.text4,marginTop:3,fontFamily:fonts.mono}}>Training · 6-12 typ.</div>
          </F>
          <F label="OOS Monate">
            <input type="number" value={wfOosM} onChange={e=>setWfOosM(Number(e.target.value))} min={1} max={12} style={{...inp,width:85,fontSize:15,fontWeight:700,padding:"10px 12px"}}/>
            <div style={{fontSize:10,color:theme.text4,marginTop:3,fontFamily:fonts.mono}}>Test · 2-3 typ.</div>
          </F>
          <F label="Trials/Window">
            <input type="number" value={wfTrials} onChange={e=>setWfTrials(Number(e.target.value))} min={20} max={500} step={20} style={{...inp,width:95,fontSize:15,fontWeight:700,padding:"10px 12px"}}/>
            <div style={{fontSize:10,color:theme.text4,marginTop:3,fontFamily:fonts.mono}}>200-300 typ.</div>
          </F>
          {/* v4: WF Mode fest auf "tuning" — kein Dropdown mehr */}
          <div style={{marginLeft:"auto"}}>
            {wf.running?<Button danger onClick={stopWf} style={{padding:"12px 24px",fontSize:14,fontWeight:800}}>Stop</Button>:<Button primary onClick={runWf} style={{padding:"12px 24px",fontSize:14,fontWeight:800}}>Start Walk-Forward</Button>}
          </div>
        </div>
        {/* FIX WF-B2 v2: Custom date range toggle — hidden by default, full data auto-derived */}
        <div style={{marginTop:12,paddingTop:10,borderTop:`1px dashed ${theme.divider}`,display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:12,color:theme.text2}}>
            <input type="checkbox" checked={wfUseCustomRange} onChange={e=>setWfUseCustomRange(e.target.checked)} style={{cursor:"pointer"}}/>
            <span>Zeitraum einschränken</span>
          </label>
          {wfUseCustomRange && <>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:10,fontWeight:700,color:theme.text4,letterSpacing:".4px",textTransform:"uppercase"}}>Start</span>
              <input type="date" value={wfStart} onChange={e=>setWfStart(e.target.value)} style={{...inp,width:155,fontSize:13,fontWeight:600,padding:"8px 10px"}}/>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:6}}>
              <span style={{fontSize:10,fontWeight:700,color:theme.text4,letterSpacing:".4px",textTransform:"uppercase"}}>Ende</span>
              <input type="date" value={wfEnd} onChange={e=>setWfEnd(e.target.value)} style={{...inp,width:155,fontSize:13,fontWeight:600,padding:"8px 10px"}}/>
            </div>
          </>}
          {!wfUseCustomRange && <span style={{fontSize:11,color:theme.text4,fontStyle:"italic"}}>Standard: volle Datenperiode (empfohlen für aussagekräftige Statistik)</span>}
        </div>
        {/* R7p: Live-Preview wie viele Fenster entstehen */}
        {!wf.running && wfIsM && wfOosM && wfTrials && (() => {
          // FIX WF-B2 v2: totalMonths exakt nur wenn User custom range gewählt hat.
          // Sonst grobe Assumption ~51 Monate — tatsächlicher Wert wird vom Backend auto-derived.
          let totalMonths = 51; // grobe Annahme für MGC als Preview-Fallback
          if (wfUseCustomRange && wfStart && wfEnd) {
            const msPerMonth = 1000 * 60 * 60 * 24 * 30.44;
            totalMonths = Math.max(1, Math.floor((new Date(wfEnd) - new Date(wfStart)) / msPerMonth));
          }
          const nWindows = Math.max(1, Math.floor((totalMonths - wfIsM) / wfOosM));
          const totalTrials = nWindows * wfTrials;
          const estMinutes = Math.max(1, Math.round(totalTrials / 200)); // ~200 trials/min on 8 cores n_jobs=7
          const rangeHint = (wfUseCustomRange && wfStart && wfEnd) ? `${wfStart} → ${wfEnd}` : "volle Datenperiode (auto)";
          return <div style={{marginTop:14,paddingTop:12,borderTop:`1px solid ${theme.divider}`,fontSize:11,color:theme.text3,fontFamily:fonts.mono,lineHeight:1.6}}>
            <strong style={{color:theme.text}}>Vorschau:</strong> ~{nWindows} Fenster · {totalTrials.toLocaleString()} Trials gesamt · ca. {estMinutes} Min auf 8 Cores · Range: {rangeHint}
            {wfMode==="full_search" && <span style={{color:theme.amberText}}> ·  full_search verdoppelt Laufzeit</span>}
          </div>;
        })()}
      </Card>
      {(wf.running||wf.windows?.length>0)&&<>
        <Card style={{marginBottom:16}} className="sl-fadein"><CardHeader title="Walk-Forward" sub={wf.running?`Window ${wf.windows_completed||0}/${wf.total_windows||"?"}`:`${(wf.windows||[]).length} windows`} right={<div style={{display:"flex",alignItems:"center",gap:8}}><ElapsedTimer running={wf.running}/>{wf.running&&<Pill bg={theme.blueLight} color={theme.blueText}>Running</Pill>}</div>}/>
          {wf.running&&wf.progress_pct!=null&&<div style={{padding:"0 20px 14px"}}><div style={{height:6,borderRadius:3,background:theme.tint}}><div style={{height:"100%",borderRadius:3,background:theme.blue,width:`${wf.progress_pct}%`}}/></div><div style={{fontSize:11,color:theme.text3,marginTop:4}}>{wf.status_message||""}</div></div>}
        </Card>
        {wf.summary&&Object.keys(wf.summary).length>0&&<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:8,marginBottom:16}}>
          {[["Total OOS P&L",formatPnl(wf.summary.total_oos_pnl),(wf.summary.total_oos_pnl||0)>0?theme.greenText:theme.redText],["Window WR",`${wf.summary.window_win_rate||0}%`,(wf.summary.window_win_rate||0)>60?theme.greenText:theme.redText],["Avg OOS PF",Number(wf.summary.avg_oos_pf||0).toFixed(2),(wf.summary.avg_oos_pf||0)>1.3?theme.greenText:theme.text],["Robustness",wf.summary.robustness||"—",wf.summary.robustness==="ROBUST"?theme.greenText:theme.redText]].map(([l,v,c])=>
            <Card key={l} className="sl-card sl-fadein" style={{padding:"10px 14px"}}><div style={{fontSize:8.5,fontWeight:700,color:theme.text4,textTransform:"uppercase",letterSpacing:".6px",marginBottom:4}}>{l}</div><div style={{fontSize:15,fontWeight:800,fontFamily:fonts.mono,color:c,lineHeight:1}}>{v}</div></Card>
          )}
        </div>}
        {/* v4: Single Backend-Truth Verdict Banner */}
        {(() => {
          const s = wf.summary;
          if (!s || !stratFile) return null;
          const robustness = s.robustness;
          if (robustness === "ROBUST") {
            const xi = s.cross_instrument;
            return (
              <div style={{padding:"16px 20px",borderRadius:12,marginBottom:18,background:theme.greenLight,border:"1px solid rgba(22,163,74,0.15)"}}>
                <div style={{fontSize:15,fontWeight:800,color:theme.greenText,marginBottom:4}}>🚀 ROBUST — alle OOS-Constraints erfüllt</div>
                <div style={{fontSize:13,color:theme.text2}}>
                  Aggregate Degradation: <strong>{s.aggregate_degradation_pct}%</strong> · Median Window: <strong>{s.median_window_degradation_pct}%</strong> · Profitable Windows: <strong>{s.profitable_windows}/{s.total_windows}</strong>
                  {xi && <><br/>Cross-Instrument: <strong>{xi.profitable_count}/{xi.instruments_tested}</strong> profitable ({xi.passed ? "✓ passed" : "✗ failed"})</>}
                </div>
                <div style={{display:"flex",gap:8,marginTop:10}}>
                  <Button onClick={async()=>{const r=await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(stratFile)}?_t=${Date.now()}`);const d=await r.json();setVerifyConfig(d.config||d);setShowVerify(true);}} style={{fontSize:13}}>🔍 Verifizieren</Button>
                  <Button primary style={{background:theme.green,padding:"10px 24px",fontSize:14}} onClick={()=>showModal("Go Live",`${stratFile} live schalten? Bot-Neustart erforderlich!`,async()=>{const res=await postApi(`/strategy-files/${encodeURIComponent(stratFile)}/promote`);if(res&&!res.detail){toast(res.message||"Live!");refetchF?.();}else toast(res?.detail||"Error","error");})}>Go Live</Button>
                </div>
              </div>
            );
          }
          if (robustness === "FRAGILE") {
            return (
              <div style={{padding:"16px 20px",borderRadius:12,marginBottom:18,background:theme.redLight,border:"1px solid rgba(239,68,68,0.12)"}}>
                <div style={{fontSize:15,fontWeight:800,color:theme.redText,marginBottom:6}}>⚠ FRAGILE — NICHT live setzen</div>
                <div style={{fontSize:12,color:theme.text2,marginBottom:8}}>Eine oder mehrere OOS-Constraints verletzt:</div>
                <ul style={{margin:"4px 0 0 18px",fontSize:12,color:theme.text2,lineHeight:1.7}}>
                  {(s.constraint_violations||[]).map((v,i) => <li key={i}>{v}</li>)}
                </ul>
              </div>
            );
          }
          if (robustness === "UNRELIABLE") {
            return (
              <div style={{padding:"16px 20px",borderRadius:12,marginBottom:18,background:theme.amberLight,border:"1px solid rgba(245,158,11,0.2)"}}>
                <div style={{fontSize:15,fontWeight:800,color:theme.amberText,marginBottom:6}}>📊 UNRELIABLE — nicht genug Sample-Size</div>
                <div style={{fontSize:13,color:theme.text2}}>{s.reliable_windows_pct}% reliable windows (benötigt ≥60%). Run mit längerem IS-Fenster oder weniger selektiver Strategie.</div>
              </div>
            );
          }
          if (robustness === "LOSING_OOS") {
            return (
              <div style={{padding:"16px 20px",borderRadius:12,marginBottom:18,background:theme.redLight,border:"1px solid rgba(239,68,68,0.12)"}}>
                <div style={{fontSize:15,fontWeight:800,color:theme.redText,marginBottom:6}}>✗ LOSING OOS — Total OOS PnL ${s.total_oos_pnl}</div>
                <div style={{fontSize:13,color:theme.text2}}>Strategie ist auf IS-Daten gefittet, verliert auf OOS. Typisches Overfit-Symptom.</div>
              </div>
            );
          }
          return null;
        })()}
                {/* Verification Panel */}
        {showVerify&&verifyConfig&&<VerificationPanel config={verifyConfig} stratFile={stratFile} onClose={()=>setShowVerify(false)}/>}
        {/* R7r-6: Old NOT-ROBUST banner removed — new honest-scoring banner above handles both cases */}
        {wf.windows&&wf.windows.length>0&&<><SectionTitle>IS vs OOS</SectionTitle><Card style={{padding:"12px 16px",marginBottom:16}} className="sl-fadein"><WFBars windows={wf.windows}/></Card>
        <SectionTitle>Windows</SectionTitle><Card style={{marginBottom:18}}><div style={{overflow:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`2px solid ${theme.divider}`}}>{["W","IS","OOS","IS P&L","OOS P&L","OOS PF","OOS WR","OOS Sharpe"].map(h=><th key={h} style={{padding:"6px 8px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"left",whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>
        <tbody>{wf.windows.map(w=><tr key={w.window} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`}}>
          <td style={{padding:"5px 8px",fontSize:11,fontWeight:700,fontFamily:fonts.mono}}>W{w.window}</td>
          <td style={{padding:"5px 8px",fontSize:10,fontFamily:fonts.mono,color:theme.text3}}>{w.is_start?.slice(5)}→{w.is_end?.slice(5)}</td>
          <td style={{padding:"5px 8px",fontSize:10,fontFamily:fonts.mono,color:theme.text3}}>{w.oos_start?.slice(5)}→{w.oos_end?.slice(5)}</td>
          <td style={{padding:"5px 8px",fontSize:11,fontWeight:700,fontFamily:fonts.mono,color:theme.blueText}}>{formatPnl(w.is_pnl)}</td>
          <td style={{padding:"5px 8px",fontSize:11,fontWeight:700,fontFamily:fonts.mono,color:w.oos_pnl>=0?theme.greenText:theme.redText}}>{formatPnl(w.oos_pnl)}</td>
          <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{Number(w.oos_pf||0).toFixed(2)}</td>
          <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{w.oos_wr?.toFixed(1)}%</td>
          <td style={{padding:"5px 8px",fontSize:11,fontFamily:fonts.mono}}>{Number(w.oos_sharpe||0).toFixed(2)}</td>
        </tr>)}</tbody></table></div></Card></>}
      </>}
      <SectionTitle>Walk-Forward History</SectionTitle>
      {filterRuns(wfRuns).length>0?<div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:12}}>{filterRuns(wfRuns).map((r,i)=><RunCard key={r.id||i} run={r} onClick={r.id?()=>setWfDet(r.id):undefined} onDelete={id=>deleteRun(id,"walkforwards")} onHide={hideRun}/>)}</div>:<Empty icon="shield" title="Kein Walk-Forward" sub="Teste Robustheit auf Out-of-Sample-Daten." btn="Start" onClick={runWf}/>}
    </>)}

    {/* ═══ AI ANALYST ═══ */}
    {tab==="ai" && (() => {
      const tabLabels = {builder:"Builder",scanner:"Scanner",backtest:"Backtest",optimizer:"Optimizer",walkforward:"Walk-Forward",strategies:"Strategies"};
      const contextSuggestions = {
        builder: [`Analysiere ${inst} Profil-Eignung`, `Vergleiche Entry-Patterns für ${inst}`, `Optimale Timeframes für ${inst}?`],
        scanner: [`Welche TF-Kombi ist ideal für ${inst}?`, `M5 vs M15 Entry für ${inst}`, `Warum performen bestimmte TFs besser?`],
        backtest: [`Analysiere ${inst} Backtest-Ergebnisse`, `Schwachstellen der ${inst} Strategie?`, `Wie verbessere ich die Win-Rate?`],
        optimizer: [`Was bedeuten die Phase 1 Ergebnisse?`, `Consensus-Empfehlung für ${inst}`, `Welche Features helfen bei ${inst}?`, `Parameter-Sensitivität analysieren`],
        walkforward: [`Ist ${inst} robust genug für Live?`, `OOS vs IS Performance bewerten`, `Overfitting-Risiko einschätzen`],
        strategies: [`Vergleiche alle Strategien`, `Welche Strategie ist am profitabelsten?`, `Portfolio-Diversifikation bewerten`],
      };
      const suggestions = contextSuggestions[prevTab] || contextSuggestions.builder;
      return <div>
        {/* Back Button */}
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:18}}>
          <button onClick={() => setTab(prevTab)} style={{
            display:"flex",alignItems:"center",gap:6,padding:"8px 16px",borderRadius:8,
            border:`1px solid ${theme.border}`,background:theme.white,cursor:"pointer",
            fontSize:13,fontWeight:600,color:theme.text2,fontFamily:fonts.body,
          }}>← Zurück zu {tabLabels[prevTab]||"Pipeline"}</button>
          <div style={{fontSize:13,color:theme.text3}}>
            AI Analyst · Kontext: <strong style={{color:theme.text}}>{inst}</strong>
            {stratFile && <> · <strong style={{color:theme.blueText}}>{stratFile}</strong></>}
            {prevTab && <> · von <strong>{tabLabels[prevTab]}</strong></>}
          </div>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
          <Card>
            <CardHeader title="🤖 AI Strategy Analyst" sub={`Kontext: ${inst}${stratFile?" · "+stratFile:""} · ${pipelineStatus}`}/>
            <div style={{padding:"16px 20px"}}>
              <div style={{display:"flex",gap:6,marginBottom:14}}>
                <select value={inst} onChange={e=>setInst(e.target.value)} style={{...sel,width:"auto",fontSize:13}}>{ALL_INSTRUMENTS.map(i=><option key={i} value={i}>{i}</option>)}</select>
                <input value={aiQ} onChange={e=>setAiQ(e.target.value)} placeholder="Frage stellen…" onKeyDown={e=>e.key==="Enter"&&runAi()} style={{flex:1,padding:"10px 14px",borderRadius:10,border:`1px solid ${theme.border}`,fontSize:14,fontFamily:fonts.body,outline:"none"}}/>
                <Button primary onClick={runAi} disabled={aiLd} style={{padding:"10px 20px",fontSize:13}}>{aiLd?"…":"Fragen"}</Button>
              </div>
              <div style={{fontSize:12,fontWeight:700,color:theme.text2,marginBottom:8}}>Vorschläge basierend auf {tabLabels[prevTab]||"Pipeline"}:</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
                {suggestions.map(q=><button key={q} onClick={()=>{setAiQ(q);}} style={{fontSize:12,padding:"6px 12px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.white,color:theme.text2,cursor:"pointer",fontFamily:fonts.body,transition:"all 0.15s"}}>{q}</button>)}
              </div>
            </div>
          </Card>
          <Card>
            <CardHeader title="Ergebnis"/>
            <div style={{padding:"16px 20px",minHeight:300}}>
              {aiLd?<LoadingState/>:aiRes?<div style={{fontSize:14,lineHeight:1.8,color:theme.text2,whiteSpace:"pre-wrap"}}>{typeof aiRes==="string"?aiRes:aiRes.analysis||aiRes.response||aiRes.message||JSON.stringify(aiRes,null,2)}</div>:<div style={{textAlign:"center",padding:50,color:theme.text4}}>
                <div style={{fontSize:36,marginBottom:12}}>🤖</div>
                <div style={{fontSize:15,fontWeight:700,marginBottom:6}}>AI Strategy Analyst</div>
                <div style={{fontSize:13,lineHeight:1.6,maxWidth:300,margin:"0 auto"}}>Stelle eine Frage zu deiner Strategie, den Ergebnissen oder dem aktuellen Pipeline-Schritt.</div>
              </div>}
            </div>
          </Card>
        </div>
      </div>;
    })()}

    {/* ═══ ANALYZE ═══ */}
    {tab==="analyze" && <AnalyzeTab stratFile={stratFile}/>}

    {/* ═══ DEPLOY ═══ */}
    {tab==="deploy" && <DeployTab stratFile={stratFile} onNavigate={handleTabChange}/>}

    {/* ═══ STRATEGIES ═══ */}
    {tab==="strategies" && <>
      <SectionTitle>Strategy Files ({files.length})</SectionTitle>
      {files.length>0?<Card><div style={{overflow:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr style={{borderBottom:`2px solid ${theme.divider}`}}>
        {["File","Instrument","TFs","Pipeline","Status","Version","Aktionen"].map(h=><th key={h} style={{padding:"8px 14px",fontSize:9,fontWeight:700,color:theme.text4,textTransform:"uppercase",textAlign:"left"}}>{h}</th>)}
      </tr></thead>
      <tbody>{files.map((f,i)=>{const n=typeof f==="string"?f:f.filename||f.name||"";const tfs=typeof f.timeframes==="object"?f.timeframes:{};const pl=f.pipeline||{status:"created",history:[]};const plColors={created:theme.tint,tf_scanned:theme.blueLight,structure_tested:theme.purpleLight,optimized:theme.amberLight,validated:theme.greenLight};const plTextColors={created:theme.text4,tf_scanned:theme.blueText,structure_tested:theme.purpleText,optimized:theme.amberText,validated:theme.greenText};
      return<tr key={i} className="sl-row" style={{borderBottom:`1px solid ${theme.divider}`,background:stratFile===n?"rgba(37,99,235,0.03)":"transparent"}}>
        <td style={{padding:"8px 14px"}}><div style={{fontSize:12,fontWeight:600,fontFamily:fonts.mono}}>{String(n)}</div>{f.created_at&&<div style={{fontSize:9,color:theme.text4,marginTop:2}}>{fDateShort(f.created_at)}</div>}
          {/* Pipeline History */}
          {pl.history&&pl.history.length>0&&<div style={{marginTop:3,display:"flex",gap:3,flexWrap:"wrap"}}>{pl.history.slice(-3).map((h,hi)=><span key={hi} style={{fontSize:8,padding:"1px 5px",borderRadius:3,background:theme.tint,color:theme.text4,fontFamily:fonts.mono}}>{h.step?.replace("_"," ")} {fDateShort(h.timestamp)}</span>)}</div>}
        </td>
        <td style={{padding:"8px 14px",fontSize:12,fontWeight:700,fontFamily:fonts.mono}}>{String(f.instrument||"—")}</td>
        <td style={{padding:"8px 14px",fontSize:10.5,fontFamily:fonts.mono,color:theme.text2}}>{tfs.entry||"?"}/{tfs.context||"?"}/{tfs.regime||"?"}</td>
        <td style={{padding:"8px 14px"}}><div style={{display:"flex",alignItems:"center",gap:4}}><Pill bg={plColors[pl.status]||theme.tint} color={plTextColors[pl.status]||theme.text4}>{String(pl.status||"created").replace("_"," ")}</Pill>{pl.history&&pl.history.length>0&&<span style={{fontSize:9,color:theme.text4}} title={pl.history.map(h=>h.step+": "+h.description).join("\n")}>{pl.history.length} steps</span>}</div></td>
        <td style={{padding:"8px 14px"}}><Pill bg={f.status==="live"?theme.greenLight:f.status==="archived"?theme.redLight:theme.tint} color={f.status==="live"?theme.greenText:f.status==="archived"?theme.redText:theme.text3}>{String(f.status||"test")}</Pill></td>
        <td style={{padding:"8px 14px",fontSize:10,fontFamily:fonts.mono,color:theme.text3}}>v{String(f.version||"?")}</td>
        <td style={{padding:"8px 14px"}}><div style={{display:"flex",gap:3}}>
          <button onClick={()=>{setStratFile(n);setInst(f.instrument||"MGC");setTab("builder");}} className="sl-btn" style={{fontSize:9,padding:"3px 7px",borderRadius:5,border:`1px solid ${theme.border}`,background:theme.white,color:theme.text2,cursor:"pointer"}}>Edit</button>
          <button onClick={()=>{setStratFile(n);setInst(f.instrument||"MGC");}} className="sl-btn" style={{fontSize:9,padding:"3px 7px",borderRadius:5,border:`1px solid ${theme.blueLight}`,background:theme.blueLight,color:theme.blueText,cursor:"pointer"}}>Select</button>
          <button onClick={async()=>{const nm=prompt("Clone als:",n.replace(".yaml","_v2.yaml"));if(nm){const res=await postApi(`/strategy-files/${encodeURIComponent(n)}/clone`,{new_filename:nm});refetchF();if(res&&!res.detail){if(res.was_reset)toast(`Cloned: ${nm} — Pipeline zurückgesetzt (startet frisch)`,"warn");else toast("Cloned: "+nm);}else toast(res?.detail||"Error","error");}}} className="sl-btn" style={{fontSize:9,padding:"3px 7px",borderRadius:5,border:`1px solid ${theme.border}`,background:theme.white,color:theme.text2,cursor:"pointer"}}>Clone</button>
          {f.status!=="live"&&pl.status==="validated"&&<button onClick={()=>showModal("Live schalten",`${n} live? Bot-Neustart!`,async()=>{const res=await postApi(`/strategy-files/${encodeURIComponent(n)}/promote`);if(res&&!res.detail){toast(res.message||"Live!");refetchF();}else toast(res?.detail||"Error","error");})} className="sl-btn" style={{fontSize:9,padding:"3px 7px",borderRadius:5,border:"1px solid rgba(22,163,74,0.2)",background:theme.greenLight,color:theme.greenText,cursor:"pointer",fontWeight:700}}>Go Live</button>}
          {f.status!=="live"&&<button onClick={()=>showModal("Löschen",`${n} archivieren?`,async()=>{await fetch(`${API_BASE}/strategy-files/${encodeURIComponent(n)}`,{method:"DELETE"});refetchF();toast("Archiviert");})} className="sl-btn" style={{fontSize:9,padding:"3px 7px",borderRadius:5,border:"1px solid rgba(220,38,38,0.15)",background:theme.redLight,color:theme.redText,cursor:"pointer"}}>Del</button>}
        </div></td>
      </tr>;})}</tbody></table></div></Card>:<div style={{padding:32,textAlign:"center",color:theme.text3}}>Keine Strategy-Dateien</div>}

      {/* Active Bots */}
      {Object.keys(strats).length>0&&<><SectionTitle style={{marginTop:20}}>Laufende Strategien ({Object.keys(strats).length})</SectionTitle>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>{Object.entries(strats).map(([name,s])=>
        <Card key={name} className="sl-card"><div style={{height:3,background:s.status==="live"?`linear-gradient(90deg,${theme.greenBright},transparent)`:`linear-gradient(90deg,${theme.amber},transparent)`}}/><div style={{padding:"12px 16px"}}><div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}><div style={{fontSize:13,fontWeight:700}}>{String(name)}</div><Pill bg={s.status==="live"?theme.greenLight:theme.tint} color={s.status==="live"?theme.greenText:theme.text3}>{String(s.status||"test")}</Pill></div><div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>{[["Instrument",String(s.symbol||s.instrument||name.split("_")[0])],["TF",String(s.timeframe||"M5")],["Exchange",String(s.exchange||"IBKR")],["Adapter",s.adapter_connected?"✓":"✗"]].map(([k,v])=><div key={k}><div style={{fontSize:8,fontWeight:600,color:theme.text4,textTransform:"uppercase",letterSpacing:".5px",marginBottom:1}}>{k}</div><div style={{fontSize:11,fontWeight:600,fontFamily:fonts.mono,color:k==="Adapter"?(v==="✓"?theme.greenText:theme.redText):theme.text}}>{v}</div></div>)}</div></div></Card>
      )}</div></>}
    </>}
  </>;
}
